import React, { useState, useEffect, useMemo, useRef } from "react";
import CampaignDetailsCard from "../AdminCampaignApproval/components/CampaignDetailsCard";
import MessagePreviewSection from "./components/MessagePreviewCard";
import { OperatorOption } from "./components/ManageOperatorPopup";
import { Button } from "../../../Components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import RejectCampaignDialog from "./components/RejectCampaignDialog";
import { ChannelAllocations } from "./components/AllocationCard";
import { RootState } from "../../../State/store";
import { useSelector } from "react-redux";
import axios from "axios";
import { useToast } from "../../../Components/ui/use-toast";
import { Toaster } from "../../../Components/ui/toaster";
import { CircularProgress } from "@mui/material";
import { revertCampaignWindowFromUTC } from "../../../Pages/Campaign/CreateCampaign/utils/revertCampaignWindowFromUTC";
import { getSmsPartsCount } from "../../../Pages/Campaign/CreateCampaign/utils/getSmsPartsCount";
import ApproveWithoutWelcomeDialog from "./components/ApproveWithoutWelcomeDialog";

// // --- Consistent OperatorOption ---
// export interface OperatorOption {
//   id: number | string;                // channel_config_id
//   channel_config_id: number | string; // redundant but for type compatibility
//   partner_id: number;                 // partner_id (this is what goes as operator_id)
//   label: string;
//   auto_approval?: boolean;
//   channel_config?: string | object;
//   channel_config_name?: string;
// }

type Notification = {
  id: number;
  campaignId: number;
  statusMark: string;
  notificationType: string;
  createdAt: string;
  notificationData: Record<string, string>;
  role: string;
};

type OperatorKey = {
  channel: string;
  country: string;
  operatorId: string | number;
  label: string;
};

const channelIdToName = (id: number) => {
  if (id === 1) return "WhatsApp";
  if (id === 2) return "SMS";
  return `Channel_${id}`;
};

const isoToName = (iso: string, isoToCountryName: Record<string, string>) =>
  isoToCountryName?.[iso?.toUpperCase()] || iso;

const AdminCampaignApproval: React.FC = () => {
  const adminUrl = useSelector(
    (state: RootState) => state.authentication.adminUrl,
  );

  const operatorUrl = useSelector(
    (state: RootState) => state.authentication.operatorUrl,
  );

  const accountId = useSelector(
    (state: RootState) => state.authentication.account_id,
  );
  const workspaceId = useSelector(
    (state: RootState) => state.authentication.workspace_id,
  );
  const toast = useToast();
  const location = useLocation();
  const { campaignId, channelType, status, campaignWorkspaceId } =
    location.state || {};

  const [selectedOperators, setSelectedOperators] = useState<
    Record<string, Record<string, OperatorOption[]>>
  >({});
  const [selectedOperatorKey, setSelectedOperatorKey] =
    useState<OperatorKey | null>(null);
  const [editedAllocations, setEditedAllocations] = useState<
    Record<string, Record<string, Record<string, number>>>
  >({});
  const [activeChannel, setActiveChannel] = useState<string>("");
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [campaignStatus, setCampaignStatus] = useState<string>("Pending");
  const [rejectedWorkspaceId, setRejectedWorkspaceId] = useState<number>(0);
  //const [campaignApprovalStatus, setCampaignApprovalStatus] =
  useState<boolean>(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectComment, setRejectComment] = useState("");

  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [approveDialogMessage, setApproveDialogMessage] = useState("");

  const [operatorTemplates, setOperatorTemplates] = useState<
    Record<string, Record<string, Record<string, any>>>
  >({});
  const [campaignName, setCampaignName] = useState<string>("");
  const [campaignDesc, setCampaignDesc] = useState<string>("");
  const [fromCountryIso, setFromCountryIso] = useState<string[]>([]);
  const [toCountryIso, setToCountryIso] = useState<string>("");
  const [fromCountryNames, setFromCountryNames] = useState<string[]>([]);
  const [toCountryName, setToCountryName] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [startTime, setStartTime] = useState<string>("");
  const [endTime, setEndTime] = useState<string>("");
  const [rawStartDate, setRawStartDate] = useState<string>(""); // for DB/API
  const [rawEndDate, setRawEndDate] = useState<string>("");
  const [rawStartTime, setRawStartTime] = useState<string>("");
  const [rawEndTime, setRawEndTime] = useState<string>("");
  const [totalTravellerCount, setTotalTravellerCount] = useState<any>(null);
  const [premiumNumber, setPremiumNumber] = useState<boolean>(false);
  const [duplicateMsg, setDuplicateMsg] = useState<boolean>(false);
  const [allocationType, setAllocationType] = useState<string>("");
  const [audienceType, setAudienceType] = useState<string>("");
  const [selectedCampaignName, setSelectedCampaignName] = useState<string>("");
  const [audienceConfig, setAudienceConfig] = useState<any>(null);
  const [roamingAge, setRoamingAge] = useState<string>("");
  const [campaignStatusFromApi, setCampaignStatusFromApi] =
    useState<string>("");
  const [userAccountId, setUserAccountId] = useState<number | null>(null);
  const [advertiserWorkspaceId, setAdvertiserWorkspaceId] = useState<
    number | null
  >(null);
  const [isInitialPageLoading, setIsInitialPageLoading] = useState(true);
  const [isMappingLoading, setIsMappingLoading] = useState(false);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [categorySubId, setCategorySubId] = useState<number | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [availableChannels, setAvailableChannels] = useState<
    { channel_id: number; channel_name: string }[]
  >([]);
  const [isoToCountryName, setIsoToCountryName] = useState<
    Record<string, string>
  >({});
  const [countryToIso, setCountryToIso] = useState<Record<string, string>>({});
  const [isoToCountryCode, setIsoToCountryCode] = useState<
    Record<string, string>
  >({});
  const [channelNameToId, setChannelNameToId] = useState<
    Record<string, number>
  >({});

  const [initialTemplateNamesByChannel, setInitialTemplateNamesByChannel] =
    useState<Record<string, string>>({});

  const [allocations, setAllocations] = useState<ChannelAllocations>({});
  const [advertiserEmail, setAdvertiserEmail] = useState("");
  const [campaignStatusForSandbox, setCampaignStatusForSandbox] = useState("");
  const [toCountryTimezone, setToCountryTimezone] = useState<string>("");
  const adminsworkspaceName = useSelector(
    (state: RootState) => state.authentication.workspaceName,
  );
  const admin_workspace_id = useSelector(
    (state: RootState) => state.authentication.workspace_id,
  );
  const apiUrlAdvAcc = useSelector(
    (state: RootState) => state.authentication.apiURL,
  );
  const [campaignComment, setCampaignComment] = useState<string | null>(null);
  const CurrentAID = useSelector(
    (state: RootState) => state.authentication.account_id,
  );
  // At the top of your component
  const [roamingDaysByCountry, setRoamingDaysByCountry] = React.useState<
    Record<string, any>
  >({});
  const [fromCountryISOs, setFromCountryISOs] = React.useState<
    Record<string, string>
  >({});
  const [fromCountries, setFromCountries] = React.useState<string[]>([]);

  const [isDeleted, setIsDeleted] = useState(false);

  const ttlCacheRef = useRef<Record<number, number>>({});

  const getTTLSeconds = async (channelConfigId: number) => {
    if (ttlCacheRef.current[channelConfigId]) {
      return ttlCacheRef.current[channelConfigId];
    }

    let configId = channelConfigId;
    const res = await axios.get(`${operatorUrl}/GetChannelConfigByConfigId`, {
      params: { configId },
    });

    const config = JSON.parse(res.data.channelConfigDetails.channel_config);

    const ttlSeconds = Number(config?.max_validity_hours ?? 0) * 3600;

    ttlCacheRef.current[channelConfigId] = ttlSeconds;

    return ttlSeconds;
  };

  function fixHeaderComponent(comp: any): any {
    if (comp.type === "HEADER") {
      const format = (comp.format || "").toUpperCase();
      if (format === "TEXT") {
        // Remove 'example' if present
        const { example, ...rest } = comp;
        return { ...rest, format: "TEXT" };
      } else {
        // Always set example for IMAGE/VIDEO/DOCUMENT etc.
        return {
          ...comp,
          format,
          example: { header_handle: [""] },
        };
      }
    }
    return comp;
  }

  // const campaignId = 29;

  function getTravellerISOs(totalTravellerCount: any) {
    const isoSet = new Set<string>();
    if (totalTravellerCount?.whatsapp_count) {
      totalTravellerCount.whatsapp_count.forEach((item: any) => {
        if (item.fromcountry) isoSet.add(item.fromcountry);
      });
    }
    if (totalTravellerCount?.sms_count) {
      totalTravellerCount.sms_count.forEach((item: any) => {
        if (item.fromcountry) isoSet.add(item.fromcountry);
      });
    }
    return Array.from(isoSet);
  }

  // useEffect(() => {
  const fetchCampaignDetails = async () => {
    try {
      const response = await axios.get(
        `${adminUrl}/GetCampaignDetailsById?campaignId=${campaignId}`,
      );

      if (response.data.status !== "Success") return;

      const d = response.data.campaignDetails;

      // Basic campaign info
      setCampaignName(d.campaign_name ?? "");
      setAdvertiserEmail(d.advertiser_email ?? "");
      setToCountryTimezone(d.selected_to_country_timezone ?? "");
      setRawStartDate(d.start_date || "");
      setRawStartTime(d.start_time || "");
      setRawEndDate(d.end_date || "");
      setRawEndTime(d.end_time || "");

      const cleanedTimezone =
        d.selected_to_country_timezone?.split(" ")?.[0] || "UTC";

      const { localStartDate, localStartTime, localEndDate, localEndTime } =
        revertCampaignWindowFromUTC(
          d.start_date,
          d.start_time,
          d.end_date,
          d.end_time,
          cleanedTimezone,
        );

      setCampaignDesc(d.campaign_desc ?? "");
      setStartDate(localStartDate ?? "");
      setEndDate(localEndDate ?? "");
      setStartTime(localStartTime ?? "");
      setEndTime(localEndTime ?? "");

      // From and To countries
      const fromIso: string[] = Array.isArray(d.from_country_iso)
        ? d.from_country_iso
        : [];
      const toIso: string =
        typeof d.to_country_iso === "string" ? d.to_country_iso : "";

      // Get country names & codes from API
      let isoToCountryNameMap: Record<string, string> = {};
      let isoToCountryCodeMap: Record<string, string> = {};
      let countryToIsoMap: Record<string, string> = {};

      if (fromIso.length > 0 || toIso) {
        const allISOs = Array.from(
          new Set([...fromIso, toIso].filter(Boolean)),
        );

        try {
          const countryRes = await axios.post(
            `${adminUrl}/GetCountryListByIso`,
            {
              IsoCodes: allISOs,
            },
          );

          if (
            countryRes.data.status === "Success" &&
            Array.isArray(countryRes.data.countries)
          ) {
            isoToCountryNameMap = Object.fromEntries(
              countryRes.data.countries.map((c: any) => [
                c.country_iso.toUpperCase(),
                c.country_name,
              ]),
            );
            isoToCountryCodeMap = Object.fromEntries(
              countryRes.data.countries.map((c: any) => [
                c.country_iso.toUpperCase(),
                c.country_code,
              ]),
            );
            countryToIsoMap = Object.fromEntries(
              countryRes.data.countries.map((c: any) => [
                c.country_name,
                c.country_iso.toLowerCase(),
              ]),
            );
          }
        } catch {
          // fallback
          isoToCountryNameMap = Object.fromEntries(
            fromIso.map((iso) => [iso.toUpperCase(), iso]),
          );
        }
      }

      setIsoToCountryName(isoToCountryNameMap);
      setIsoToCountryCode(isoToCountryCodeMap);
      setCountryToIso(countryToIsoMap);
      setToCountryIso(toIso);

      setFromCountryNames(
        fromIso.map((iso) => isoToCountryNameMap[iso.toUpperCase()] || iso),
      );
      setToCountryName(isoToCountryNameMap[toIso.toUpperCase()] || toIso);

      // Total traveller count
      let travellerData;
      try {
        travellerData =
          typeof d.total_traveller_count === "string"
            ? JSON.parse(d.total_traveller_count)
            : d.total_traveller_count;
        setTotalTravellerCount(travellerData ?? null);
      } catch {
        setTotalTravellerCount(null);
      }

      // Campaign settings
      setPremiumNumber(Boolean(d.premium_number));
      setDuplicateMsg(Boolean(d.duplicate_msg));
      setAllocationType(d.allocation_type ?? "");
      setUserAccountId(d.user_account_id ?? null);
      setAdvertiserWorkspaceId(d.advertiser_workspace_id ?? null);
      setCategoryId(d.category_id ?? null);
      setCategorySubId(d.category_sub_id ?? null);
      setCampaignStatusFromApi(d.status ?? null);
      setTemplates(Array.isArray(d.templates) ? d.templates : []);
      setAvailableChannels(
        Array.isArray(d.availablechannels) ? d.availablechannels : [],
      );

      // Initial template names by channel
      const initialNames: Record<string, string> = {};
      (Array.isArray(d.templates) ? d.templates : []).forEach((t: any) => {
        const channel = (d.availablechannels || []).find(
          (c: any) => c.channel_id === t.channel_id,
        )?.channel_name;
        if (channel && t.template_name) {
          initialNames[channel] = t.template_name;
        } else if (channel) {
          initialNames[channel] =
            t.template?.name || t.template_content?.name || d.campaign_name;
        }
      });
      setInitialTemplateNamesByChannel(initialNames);

      // ---- Build dynamic props for CountryRoamingDaysAccordion ----

      // Parse roaming_age JSON
      let parsedRoamingAge: Record<string, any> = {};
      if (d.roaming_age) {
        try {
          parsedRoamingAge = JSON.parse(d.roaming_age); // bj, fr, etc.
        } catch (error) {
          console.error("Error parsing roaming_age:", error);
        }
      }

      // Map ISO codes to proper uppercase
      const roamingDaysByCountry = Object.fromEntries(
        Object.entries(parsedRoamingAge).map(([iso, config]) => [
          iso.toUpperCase(),
          config,
        ]),
      );

      // fromCountryISOs: { "Benin": "BJ", "France": "FR" }
      const fromCountryISOs = fromIso.reduce((acc: any, iso) => {
        const countryName = isoToCountryNameMap[iso.toUpperCase()] || iso;
        acc[countryName] = iso.toUpperCase();
        return acc;
      }, {});

      // fromCountries array
      const fromCountries = fromIso.map(
        (iso) => isoToCountryNameMap[iso.toUpperCase()] || iso,
      );
      // Parse audience_config JSON
      let parsedAudienceConfig: any = null;
      if (d.audience_config) {
        try {
          parsedAudienceConfig =
            typeof d.audience_config === "string"
              ? JSON.parse(d.audience_config)
              : d.audience_config;
        } catch (error) {
          console.error("Error parsing audience_config:", error);
        }
      }

      // Set audience type and selected campaign name
      if (parsedAudienceConfig) {
        setAudienceType(parsedAudienceConfig.type || "");
        setSelectedCampaignName(d.linked_campaign_name || "");
        setAudienceConfig(parsedAudienceConfig); // Add this line
      } else {
        setAudienceType("");
        setSelectedCampaignName("");
        setAudienceConfig(null); // Add this line
      }

      // ---- REUSE AUDIENCE: Pre-fill selectedOperators + editedAllocations ----
      if (
        parsedAudienceConfig?.type === "Reuse Audience" &&
        Array.isArray(d.reuse_audience_operators)
      ) {
        const newSelectedOperators: Record<
          string,
          Record<string, OperatorOption[]>
        > = {};
        const newEditedAllocations: Record<
          string,
          Record<string, Record<string, number>>
        > = {};

        d.reuse_audience_operators.forEach((op: any) => {
          // Step 1: Convert channel_id → channel name
          const channelName = op.channel_id === 1 ? "WhatsApp" : "SMS";

          // Step 2: Convert from_country_iso → country name
          const countryName =
            isoToCountryNameMap[op.from_country_iso?.toUpperCase()] ||
            op.from_country_iso;

          // Step 3: Build selectedOperators
          if (!newSelectedOperators[channelName])
            newSelectedOperators[channelName] = {};
          if (!newSelectedOperators[channelName][countryName])
            newSelectedOperators[channelName][countryName] = [];

          newSelectedOperators[channelName][countryName].push({
            id: op.channel_config_id,
            channel_config_id: op.channel_config_id,
            label: op.channel_config_name,
            channel_config_name: op.channel_config_name,
            partner_id: op.partner_id,
            auto_approval: op.auto_approval,
            channel_config: op.channel_config,
            short_code: (() => {
              try {
                const config =
                  typeof op.channel_config === "string"
                    ? JSON.parse(op.channel_config)
                    : op.channel_config;
                const raw = config?.config?.sms_short_codes;
                const arr = typeof raw === "string" ? JSON.parse(raw) : raw;
                return Array.isArray(arr) ? (arr[0] ?? "") : "";
              } catch {
                return "";
              }
            })(),
          });

          // Step 4: Build editedAllocations
          if (!newEditedAllocations[channelName])
            newEditedAllocations[channelName] = {};
          if (!newEditedAllocations[channelName][countryName])
            newEditedAllocations[channelName][countryName] = {};

          newEditedAllocations[channelName][countryName][
            op.channel_config_id.toString()
          ] = op.allocation;
        });

        setSelectedOperators(newSelectedOperators);
        setEditedAllocations(newEditedAllocations);
      }

      debugger;
      // Set dynamic props in state
      setRoamingDaysByCountry(roamingDaysByCountry);
      setFromCountryISOs(fromCountryISOs);
      setFromCountries(fromCountries);

      console.log("fromCountries", fromCountries);
      console.log("fromCountryISOs", fromCountryISOs);
      console.log("roamingDaysByCountry", roamingDaysByCountry);
    } catch (err) {
      toast.toast({
        title: "Error",
        description: "Failed to fetch campaign details",
      });
      setFromCountryNames([]);
      setToCountryName("");
    }
  };

  //   const checkAndFetch = async () => {
  //     const status = await handleCheckStatus();

  //     if (status === "already approved" || status === "rejected") {
  //       fetchAdminCampaignDetails();
  //       //fetchCampaignDetails();
  //     } else {
  //       fetchCampaignDetails();
  //     }
  //   };

  //   checkAndFetch();
  // }, [campaignId, adminUrl]);

  useEffect(() => {
    const checkAndFetch = async () => {
      // 🔥 Set initial loading to true at the very start
      setIsInitialPageLoading(true);
      setIsLoading(true); // Keep this for internal loading states

      try {
        setSelectedOperatorKey(null);
        setActiveChannel("");
        const status = await handleCheckStatus();

        await handleCheckDeletedStatus();

        if (
          status === "already approved" ||
          status === "rejected" ||
          status === "mapping found" ||
          status === "mapping found and admin rejected"
        ) {
          await fetchAdminCampaignDetails();
        } else {
          await fetchCampaignDetails();
        }
      } catch (error) {
        console.error("Error in initial data fetch:", error);
      } finally {
        // 🔥 Set initial loading to false after everything is complete
        setIsInitialPageLoading(false);
        setIsLoading(false);
      }
    };

    if (campaignId && adminUrl) {
      checkAndFetch();
    }
  }, [campaignId, adminUrl]);

  useEffect(() => {
    if (availableChannels.length) {
      const map: Record<string, number> = {};
      availableChannels.forEach((c) => {
        map[c.channel_name] = c.channel_id;
      });
      setChannelNameToId(map);
    }
  }, [availableChannels]);

  useEffect(() => {
    if (availableChannels.length) {
      if (availableChannels.some((ch) => ch.channel_name === "WhatsApp")) {
        setActiveChannel("WhatsApp");
      } else if (availableChannels.some((ch) => ch.channel_name === "SMS")) {
        setActiveChannel("SMS");
      } else {
        setActiveChannel(availableChannels[0]?.channel_name || "");
      }
    }
  }, [availableChannels]);

  // useEffect(() => {
  //   if (availableChannels.length && (campaignStatus === "already approved" || campaignStatus === "rejected")) {
  //     // For approved/rejected campaigns, set active channel from available channels
  //     const firstChannel = availableChannels[0]?.channel_name;
  //     if (firstChannel && !activeChannel) {
  //       setActiveChannel(firstChannel);
  //       console.log(`Setting active channel to: ${firstChannel}`);
  //     }
  //   }
  // }, [availableChannels, campaignStatus, activeChannel]);

  // useEffect(() => {
  //   if ((campaignStatus === "already approved" || campaignStatus === "rejected") &&
  //     activeChannel &&
  //     Object.keys(selectedOperators).length > 0 &&
  //     !selectedOperatorKey) {

  //     // Find the first operator in the active channel
  //     const channelOperators = selectedOperators[activeChannel];
  //     if (channelOperators) {
  //       const firstCountry = Object.keys(channelOperators)[0];
  //       if (firstCountry && channelOperators[firstCountry].length > 0) {
  //         const firstOperator = channelOperators[firstCountry][0];
  //         const newKey: OperatorKey = {
  //           channel: activeChannel,
  //           country: firstCountry,
  //           operatorId: firstOperator.id,
  //           label: firstOperator.label || firstOperator.channel_config_name || "Operator"
  //         };
  //         setSelectedOperatorKey(newKey);
  //         console.log(`Setting selectedOperatorKey for approved/rejected campaign:`, newKey);
  //       }
  //     }
  //   }
  // }, [campaignStatus, activeChannel, selectedOperators, selectedOperatorKey]);

  useEffect(() => {
    // 🔥 FIX: Only run this for pending campaigns
    if (
      campaignStatus === "already approved" ||
      campaignStatus === "rejected"
    ) {
      return;
    }

    if (!totalTravellerCount || Object.keys(isoToCountryName).length === 0) {
      setAllocations({});
      return;
    }
    const allocationsObj: ChannelAllocations = {};
    Object.entries(totalTravellerCount).forEach(
      ([channelKey, countryArray]) => {
        let channelLabel = channelKey;
        if (channelLabel.endsWith("_count"))
          channelLabel = channelLabel.replace("_count", "");
        if (channelLabel.toLowerCase() === "whatsapp")
          channelLabel = "WhatsApp";
        if (channelLabel.toLowerCase() === "sms") channelLabel = "SMS";
        allocationsObj[channelLabel] = (countryArray as any[]).map((item) => ({
          country:
            isoToCountryName[item.fromcountry?.toUpperCase()] ||
            item.fromcountry,
          travellers: item.count,
          operators: [],
        }));
      },
    );
    setAllocations(allocationsObj);
  }, [totalTravellerCount, isoToCountryName, campaignStatus]);

  // Replace the getApiTemplateContent function in AdminCampaignApproval.tsx with this fixed version:

  const getApiTemplateContent = (channelName: string) => {
    // For approved/rejected campaigns, get from operatorTemplates using selectedOperatorKey
    if (
      (campaignStatus === "already approved" ||
        campaignStatus === "rejected") &&
      selectedOperatorKey
    ) {
      const { channel, country, operatorId } = selectedOperatorKey;

      // Debug logging to see what we're looking for vs what exists
      console.log(
        `🔍 [API TEMPLATE DEBUG] Looking for: ${channelName}/${country}/${operatorId}`,
      );
      console.log(
        `🔍 [API TEMPLATE DEBUG] Available channels:`,
        Object.keys(operatorTemplates),
      );
      console.log(
        `🔍 [API TEMPLATE DEBUG] Available countries for ${channelName}:`,
        Object.keys(operatorTemplates[channelName] || {}),
      );
      console.log(
        `🔍 [API TEMPLATE DEBUG] Available operators for ${channelName}/${country}:`,
        Object.keys(operatorTemplates[channelName]?.[country] || {}),
      );

      // Try exact match first
      let template = operatorTemplates[channelName]?.[country]?.[operatorId];

      if (!template) {
        // Try string conversion of operatorId
        const operatorIdStr = String(operatorId);
        template = operatorTemplates[channelName]?.[country]?.[operatorIdStr];
        console.log(
          `🔍 [API TEMPLATE DEBUG] Trying string version: ${operatorIdStr}`,
          !!template,
        );
      }

      if (!template) {
        // Try number conversion of operatorId
        const operatorIdNum = Number(operatorId);
        if (!isNaN(operatorIdNum)) {
          template = operatorTemplates[channelName]?.[country]?.[operatorIdNum];
          console.log(
            `🔍 [API TEMPLATE DEBUG] Trying number version: ${operatorIdNum}`,
            !!template,
          );
        }
      }

      if (!template) {
        // For SMS, try to find any template in the country for this channel
        if (channelName.toLowerCase() === "sms") {
          const countryTemplates =
            operatorTemplates[channelName]?.[country] || {};
          const availableOperatorIds = Object.keys(countryTemplates);
          if (availableOperatorIds.length > 0) {
            const firstAvailableId = availableOperatorIds[0];
            template = countryTemplates[firstAvailableId];
            console.log(
              `🔍 [API TEMPLATE DEBUG] SMS fallback: using operator ${firstAvailableId}`,
              !!template,
            );
          }
        }
      }

      if (!template) {
        console.log(
          `❌ [API TEMPLATE DEBUG] No template found for ${channelName}/${country}/${operatorId}`,
        );
        // Return empty structure that won't break the preview
        return channelName.toLowerCase() === "sms"
          ? { message: "", template: { message: "", name: "" } }
          : { components: [], template: { components: [] } };
      }

      console.log(
        `✅ [API TEMPLATE DEBUG] Retrieved template for ${channelName}:`,
        template,
      );
      return template;
    }

    debugger;

    // For pending campaigns, get from templates array (original logic)
    const tpl = templates.find((t) => {
      const channelNameFound = availableChannels.find(
        (c) => c.channel_id === t.channel_id,
      )?.channel_name;
      return channelNameFound?.toLowerCase() === channelName.toLowerCase();
    });

    if (!tpl) return {};

    if (channelName.toLowerCase() === "sms") {
      const content = tpl.template_content;

      // Enhanced SMS content extraction for pending campaigns
      let smsMessage = "";
      if (content.message) {
        smsMessage = content.message;
      } else if (content.template && content.template.message) {
        smsMessage = content.template.message;
      } else if (content.translated_template?.template?.message) {
        smsMessage = content.translated_template.template.message;
      }

      return {
        ...content,
        message: smsMessage,
        name: content.name || content.template?.name || `${campaignName}_sms`,
      };
    } else if (channelName.toLowerCase() === "whatsapp") {
      const templateContent = tpl.template_content;
      let mediaBase64 = "";

      if (tpl.media_config && tpl.media_config.binary_data) {
        mediaBase64 = `data:${
          tpl.media_config.file_type
        };base64,${tpl.media_config.binary_data.replace(/^data:[^,]+,/, "")}`;
      }

      return {
        ...templateContent,
        Base64: mediaBase64,
        media_config: tpl.media_config,
        qr: tpl.qr || [],
      };
    }

    return tpl.template_content;
  };

  useEffect(() => {
    // 🔥 FIX: Only run this useEffect for PENDING campaigns, not for approved/rejected ones
    if (
      campaignStatus === "already approved" ||
      campaignStatus === "rejected" ||
      campaignStatus === "mapping found" ||
      campaignStatusForSandbox === "sandbox"
    ) {
      console.log(
        "⏭️ [TEMPLATE GENERATION] Skipping template generation for approved/rejected campaign",
      );
      return;
    }

    if (
      !Object.keys(allocations).length ||
      !Object.keys(selectedOperators).length
    )
      return;

    interface AllocationOrderEntry {
      channel: string;
      country: string;
      op: OperatorOption;
    }

    // 1. Collect global order for all configs
    const allocationOrder: AllocationOrderEntry[] = [];
    Object.keys(selectedOperators).forEach((channel) => {
      Object.keys(selectedOperators[channel]).forEach((country) => {
        selectedOperators[channel][country].forEach((op: OperatorOption) => {
          allocationOrder.push({ channel, country, op });
        });
      });
    });

    // 2. Map operatorId to allocation number
    const allocationNumMap: Record<string | number, number> = {};
    allocationOrder.forEach((entry, idx) => {
      allocationNumMap[entry.op.id] = idx + 1;
    });

    setOperatorTemplates((prev) => {
      const newTemplates: typeof prev = { ...prev };
      Object.keys(selectedOperators).forEach((channel) => {
        if (!newTemplates[channel]) newTemplates[channel] = {};
        Object.keys(selectedOperators[channel]).forEach((country) => {
          if (!newTemplates[channel][country])
            newTemplates[channel][country] = {};
          selectedOperators[channel][country].forEach((op: OperatorOption) => {
              if (newTemplates[channel][country][op.id]) {
    return;
  }
            const apiTemplate = getApiTemplateContent(channel);

            // Get base name from initial template names (without trailing _N)
            let baseName =
              initialTemplateNamesByChannel[channel] ||
              apiTemplate.name ||
              campaignName;

            // Remove any existing versioning (e.g., basithtest_whatsapp_1719_1 -> basithtest_whatsapp_1719)
            if (/_\d+$/.test(baseName)) {
              baseName = baseName.replace(/_\d+$/, "");
            }

            // Generate the allocation-based name: baseName + '_' + allocationNum
            const allocationNum = allocationNumMap[op.id];
            const templateName = `${baseName}_${allocationNum}`;

            // Store the template with the allocation-based name
            newTemplates[channel][country][op.id] = {
              ...apiTemplate,
              name: templateName,
              // Store the base allocation name for later versioning
              _baseAllocationName: templateName,
            };
            console.log(
              `[QR CHECK] ${channel}/${country}/${op.id} → qr:`,
              JSON.stringify(newTemplates[channel][country][op.id]?.qr),
            );
          });
        });
      });
      return { ...newTemplates };
    });

    // eslint-disable-next-line
  }, [
    selectedOperators,
    templates,
    availableChannels,
    allocations,
    initialTemplateNamesByChannel,
    campaignName,
    campaignStatus,
  ]);

  const operatorKeys: OperatorKey[] = useMemo(() => {
    const keys: OperatorKey[] = [];
    const channelOperators = selectedOperators[activeChannel] || {};

    Object.entries(channelOperators).forEach(([country, ops]) => {
      ops.forEach((op) => {
        keys.push({
          channel: activeChannel,
          country,
          operatorId: op.id,
          label: op.label || op.channel_config_name || "Operator",
        });
      });
    });

    return keys;
  }, [activeChannel, selectedOperators]);

  const allOperatorKeys: OperatorKey[] = useMemo(() => {
    const keys: OperatorKey[] = [];

    Object.entries(selectedOperators).forEach(([channel, countriesObj]) => {
      Object.entries(countriesObj).forEach(([country, ops]) => {
        ops.forEach((op) => {
          keys.push({
            channel,
            country,
            operatorId: op.id,
            label: op.label || op.channel_config_name || "Operator",
          });
        });
      });
    });

    return keys;
  }, [selectedOperators]);

  // Only set the default once, or when the list is changed drastically (e.g. after a fetch)
  useEffect(() => {
    setSelectedOperatorKey((current) => {
      // If nothing selected OR selected operator is not in list, pick first one
      if (
        !current ||
        !operatorKeys.some(
          (key) =>
            key.operatorId === current.operatorId &&
            key.country === current.country &&
            key.channel === current.channel,
        )
      ) {
        return operatorKeys[0] || null;
      }
      // Otherwise, don't change user selection!
      return current;
    });
    // ⚠️ Notice: only re-run if operatorKeys length changes (not values)
  }, [operatorKeys.length]);

  useEffect(() => {
    if (
      operatorKeys.length > 0 &&
      (!selectedOperatorKey ||
        !operatorKeys.some(
          (key) =>
            key.operatorId === selectedOperatorKey.operatorId &&
            key.country === selectedOperatorKey.country &&
            key.channel === selectedOperatorKey.channel,
        ))
    ) {
      setSelectedOperatorKey(operatorKeys[0]);
    }
    // else: valid selection exists, do nothing!
    // eslint-disable-next-line
  }, [JSON.stringify(operatorKeys)]);

  // useEffect(() => {
  //   // Only run after both selectedOperators and operatorTemplates are set
  //   if (
  //     (campaignStatus === "already approved" || campaignStatus === "rejected") &&
  //     Object.keys(selectedOperators).length > 0 &&
  //     Object.keys(operatorTemplates).length > 0 &&
  //     !selectedOperatorKey
  //   ) {
  //     // Loop through all operators in current activeChannel (or fallback to first channel)
  //     const currentChannel = activeChannel || Object.keys(selectedOperators)[0];
  //     if (currentChannel && selectedOperators[currentChannel]) {
  //       const countries = Object.keys(selectedOperators[currentChannel]);
  //       for (const country of countries) {
  //         const ops = selectedOperators[currentChannel][country];
  //         for (const op of ops) {
  //           if (
  //             operatorTemplates[currentChannel] &&
  //             operatorTemplates[currentChannel][country] &&
  //             operatorTemplates[currentChannel][country][op.id]
  //           ) {
  //             // Found a valid operator/template mapping: select it and return!
  //             setSelectedOperatorKey({
  //               channel: currentChannel,
  //               country,
  //               operatorId: op.id,
  //               label: op.label,
  //             });
  //             return;
  //           }
  //         }
  //       }
  //     }
  //     // Fallback: pick the first available key
  //     for (const [channel, countries] of Object.entries(selectedOperators)) {
  //       for (const [country, ops] of Object.entries(countries)) {
  //         for (const op of ops) {
  //           setSelectedOperatorKey({
  //             channel,
  //             country,
  //             operatorId: op.id,
  //             label: op.label,
  //           });
  //           return;
  //         }
  //       }
  //     }
  //   }
  //   // Do NOT auto-reset if user picked something!
  //   // eslint-disable-next-line
  // }, [
  //   campaignStatus,
  //   selectedOperators,
  //   operatorTemplates,
  //   selectedOperatorKey,
  //   activeChannel,
  // ]);

  const allChannelsValid = Object.keys(allocations).every((channel) =>
    allocations[channel].every((countryBlock) => {
      const ops = (
        selectedOperators[channel]?.[countryBlock.country] || []
      ).map((op) => {
        const edited =
          editedAllocations[channel]?.[countryBlock.country]?.[
            op.id.toString()
          ];
        return {
          ...op,
          allocation:
            typeof edited === "number"
              ? edited
              : Math.floor(
                  100 /
                    (selectedOperators[channel]?.[countryBlock.country]
                      ?.length || 1),
                ),
        };
      });
      const total = ops.reduce((sum, op) => sum + Number(op.allocation), 0);
      const hasOperator =
        ops.length > 0 && ops.some((op) => Number(op.allocation) > 0);
      return hasOperator && total === 100;
    }),
  );

  const hasRejectedOperators = Object.values(selectedOperators).some(
    (countriesObj) =>
      Object.values(countriesObj).some((operators) =>
        operators.some((op) => {
          if (campaignStatusForSandbox === "sandbox") {
            return (
              op.status?.toLowerCase() === "rejected" ||
              op.status?.toLowerCase() === "pending"
            );
          }
          return op.status === "rejected";
        }),
      ),
  );

  // Key changes in fetchAdminCampaignDetails function:

  const fetchAdminCampaignDetails = async () => {
    setSelectedOperatorKey(null);
    console.log("🚀 Starting fetchAdminCampaignDetails");

    try {
      debugger;
      const response = await axios.get(
        `${adminUrl}/GetAdminCampaignDetailsById?campaignId=${campaignId}`,
      );

      console.log("📥 Raw API Response:", response.data);

      if (response.data.status === "Success") {
        const d = response.data.campaignDetails;
        setCampaignName(d.campaign_name ?? "");

        // Build unique channels for tabs
        const uniqueChannels: { channel_id: number; channel_name: string }[] =
          [];
        const channelIds = new Set();

        if (Array.isArray(d.operators)) {
          d.operators.forEach((op: any) => {
            if (Array.isArray(op.channels)) {
              op.channels.forEach((ch: any) => {
                let channel_name = channelIdToName(ch.channel_id);
                if (!channelIds.has(ch.channel_id)) {
                  channelIds.add(ch.channel_id);
                  uniqueChannels.push({
                    channel_id: ch.channel_id,
                    channel_name,
                  });
                }
              });
            }
          });
        }

        console.log("🔗 Unique Channels:", uniqueChannels);
        setAvailableChannels(uniqueChannels);

        // Country mapping (keep existing logic)
        const fromIso = Array.isArray(d.from_country_iso)
          ? d.from_country_iso
          : [];
        const toIso =
          typeof d.to_country_iso === "string" ? d.to_country_iso : "";
        let isoToCountryNameMap: Record<string, string> = {};
        let countryToIsoMap: Record<string, string> = {};
        let isoToCountryCodeMap: Record<string, string> = {};

        const allISOsFromAPI = new Set<string>();
        if (Array.isArray(d.operators)) {
          d.operators.forEach((op: any) => {
            op.channels?.forEach((ch: any) => {
              if (ch.from_country_iso)
                allISOsFromAPI.add(ch.from_country_iso.toLowerCase());
              if (ch.to_country_iso)
                allISOsFromAPI.add(ch.to_country_iso.toLowerCase());
            });
          });
        }

        setCampaignStatusForSandbox(d.campaign_status);

        if (d.total_traveller_count) {
          for (const key of Object.keys(d.total_traveller_count)) {
            const arr = d.total_traveller_count[key];
            arr.forEach((item: any) => {
              if (item.fromcountry)
                allISOsFromAPI.add(item.fromcountry.toLowerCase());
            });
          }
        }

        const allISOs = Array.from(
          new Set(
            [...fromIso, toIso, ...Array.from(allISOsFromAPI)].filter(Boolean),
          ),
        );

        // Fetch country mappings
        if (allISOs.length > 0) {
          try {
            const countryRes = await axios.post(
              `${adminUrl}/GetCountryListByIso`,
              {
                IsoCodes: allISOs,
              },
            );
            if (
              countryRes.data.status === "Success" &&
              Array.isArray(countryRes.data.countries)
            ) {
              isoToCountryNameMap = Object.fromEntries(
                countryRes.data.countries.map((c: any) => [
                  c.country_iso.toUpperCase(),
                  c.country_name,
                ]),
              );
              isoToCountryCodeMap = Object.fromEntries(
                countryRes.data.countries.map((c: any) => [
                  c.country_iso.toUpperCase(),
                  c.country_code,
                ]),
              );
              countryToIsoMap = Object.fromEntries(
                countryRes.data.countries.map((c: any) => [
                  c.country_name,
                  c.country_iso.toUpperCase(),
                ]),
              );
              setIsoToCountryName(isoToCountryNameMap);
              setIsoToCountryCode(isoToCountryCodeMap);
              setCountryToIso(countryToIsoMap);
              setFromCountryNames(
                fromIso.map(
                  (iso: string) =>
                    isoToCountryNameMap[iso.toUpperCase()] || iso,
                ),
              );
              setToCountryName(
                isoToCountryNameMap[toIso.toUpperCase()] || toIso || "",
              );

              console.log(`🌍 [COUNTRY MAPPING DEBUG]:`, {
                isoToCountryNameMap,
                IN: isoToCountryNameMap["IN"],
                LK: isoToCountryNameMap["LK"],
              });
            }
          } catch (error) {
            console.error("Error fetching country data:", error);
          }
        }

        // Build everything in one go to avoid race conditions
        const finalAllocations: ChannelAllocations = {};
        const finalSelectedOperators: Record<
          string,
          Record<string, OperatorOption[]>
        > = {};
        const finalOperatorTemplates: Record<
          string,
          Record<string, Record<string, any>>
        > = {};

        // Build traveler count mapping
        const travelerCountMap: Record<string, Record<string, number>> = {};
        if (d.total_traveller_count) {
          for (const key of Object.keys(d.total_traveller_count)) {
            const arr = d.total_traveller_count[key];
            let channelName = "";
            if (key.toLowerCase().includes("whatsapp"))
              channelName = "WhatsApp";
            else if (key.toLowerCase().includes("sms")) channelName = "SMS";
            else continue;

            arr.forEach((item: any) => {
              const fromIso = (item.fromcountry || "").toUpperCase();
              const countryName =
                isoToCountryNameMap[fromIso] || item.fromcountry || "Unknown";
              if (!travelerCountMap[channelName])
                travelerCountMap[channelName] = {};
              travelerCountMap[channelName][countryName] = item.count || 0;
            });
          }
        }

        console.log("👥 Traveler Count Map:", travelerCountMap);

        // 🔥 FIXED: Process each channel individually to avoid template overwriting
        if (Array.isArray(d.operators)) {
          d.operators.forEach((operator: any) => {
            const opId = operator.operator_id;
            console.log(`\n🏢 Processing Operator ${opId}:`);

            if (Array.isArray(operator.channels)) {
              operator.channels.forEach((ch: any) => {
                console.log(
                  `🔍 [RAW CHANNEL DATA] Operator Mapping ${ch.operator_mapping_id}:`,
                  {
                    from_country: ch.from_country_iso,
                    template_name: ch.translated_template?.template?.name,
                    whatsapp_body:
                      ch.translated_template?.template?.components?.find(
                        (c: any) => c.type === "BODY",
                      )?.text,
                    sms_message: ch.translated_template?.template?.message,
                  },
                );

                const operatorMappingId = ch.operator_mapping_id;
                const channelId = Number(ch.channel_id);
                const channelName = channelIdToName(channelId);
                const fromCountryISO = (
                  ch.from_country_iso || ""
                ).toUpperCase();
                // 🔥 FIX 1: Use proper country mapping
                const countryName =
                  isoToCountryNameMap[fromCountryISO] ||
                  fromCountryISO ||
                  "Unknown";

                console.log(
                  `\n📱 Processing Channel for Operator Mapping ${operatorMappingId}:`,
                  {
                    operatorMappingId,
                    channelId,
                    channelName,
                    fromCountryISO,
                    countryName,
                    status: ch.status,
                  },
                );

                // Initialize structures
                if (!finalAllocations[channelName])
                  finalAllocations[channelName] = [];
                if (!finalSelectedOperators[channelName])
                  finalSelectedOperators[channelName] = {};
                if (!finalSelectedOperators[channelName][countryName])
                  finalSelectedOperators[channelName][countryName] = [];
                if (!finalOperatorTemplates[channelName])
                  finalOperatorTemplates[channelName] = {};
                if (!finalOperatorTemplates[channelName][countryName])
                  finalOperatorTemplates[channelName][countryName] = {};

                // Build allocation if not exists
                let countryBlock = finalAllocations[channelName].find(
                  (cb) => cb.country === countryName,
                );
                if (!countryBlock) {
                  countryBlock = {
                    country: countryName,
                    travellers:
                      travelerCountMap[channelName]?.[countryName] || 0,
                    operators: [],
                  };
                  finalAllocations[channelName].push(countryBlock);
                }

                // 🔥 FIXED: Use operator_mapping_id as the unique ID for OperatorOption
                const operatorOption: OperatorOption = {
                  id: operatorMappingId,
                  channel_config_id: ch.channel_config_id,
                  label:
                    ch.channel_config_name ||
                    ch.translated_template?.api_vendor ||
                    ch.api_vendor ||
                    `Operator ${operatorMappingId}`,
                  partner_id: opId,
                  channel_config: ch.translated_template || ch,
                  auto_approval:
                    ch.auto_approval === true ||
                    ch.status === "Approved" ||
                    ch.status === "Auto Approved",
                  channel_config_name:
                    ch.channel_config_name ||
                    ch.translated_template?.api_vendor ||
                    ch.api_vendor,
                  short_code:
                    ch.translated_template?.short_code || ch.short_code,
                  status: ch.status || "pending",
                  travellerCount: ch.total_traveller_count || 0,
                  operator_mapping_id: ch.operator_mapping_id,
                  welcomeMessage: ch.wm_template?.wm_enable === true,
                  wm_template_id:
                    ch.wm_template?.wm_enable === true
                      ? ch.wm_template?.wm_template_id || null
                      : null,

                  wm_template_name:
                    ch.wm_template?.wm_enable === true
                      ? ch.wm_template_name || null
                      : null,
                  wm_variables:
                    ch.wm_template?.wm_enable === true
                      ? ch.wm_template?.wm_variables || null
                      : null,
                };

                // Add operator (each mapping is separate)
                finalSelectedOperators[channelName][countryName].push(
                  operatorOption,
                );
                console.log(
                  `✅ Added operator to ${channelName}/${countryName} with ID:`,
                  operatorMappingId,
                );

                // 🔥 FIX 2: Create completely isolated template for each operator_mapping_id
                let isolatedTemplate: any = {};

                if (channelId === 1) {
                  // WhatsApp
                  console.log(
                    `🟢 [WHATSAPP] Processing operator mapping ${operatorMappingId} (${fromCountryISO})`,
                  );

                  // 🔥 FIX 3: Deep clone the current channel to prevent reference sharing
                  const channelCopy = JSON.parse(JSON.stringify(ch));
                  const translatedTemplate =
                    channelCopy.translated_template || {};
                  const template = translatedTemplate.template || {};
                  const originalMediaConfig =
                    translatedTemplate.media_config || {};

                  console.log(
                    `📷 [WHATSAPP MEDIA] Operator Mapping ${operatorMappingId}:`,
                    {
                      hasMediaConfig: !!originalMediaConfig.file_name,
                      fileName: originalMediaConfig.file_name,
                      fileType: originalMediaConfig.file_type,
                      templateName: template.name,
                      components: template.components,
                    },
                  );

                  // Create UNIQUE media config for this specific operator_mapping_id
                  const uniqueMediaConfig = originalMediaConfig.file_name
                    ? {
                        file_name: String(originalMediaConfig.file_name),
                        file_type: String(
                          originalMediaConfig.file_type || "image/jpeg",
                        ),
                        binary_data: String(
                          originalMediaConfig.binary_data || "",
                        ),
                        file_length: String(
                          originalMediaConfig.file_length || "0",
                        ),
                        _operator_mapping_id: Number(operatorMappingId),
                        _from_country_iso: String(fromCountryISO),
                        _to_country_iso: String(
                          channelCopy.to_country_iso || "",
                        ),
                        _unique_media_key: `wa_${operatorMappingId}_${fromCountryISO}_${Date.now()}_${Math.random()
                          .toString(36)
                          .substring(2, 8)}`,
                      }
                    : null;

                  // Create unique base64 for this operator
                  let uniqueBase64 = "";
                  if (uniqueMediaConfig?.binary_data) {
                    const binaryData = uniqueMediaConfig.binary_data;
                    const fileType = uniqueMediaConfig.file_type;
                    uniqueBase64 = binaryData.startsWith("data:")
                      ? binaryData
                      : `data:${fileType};base64,${binaryData}`;
                  }

                  // 🔥 FIX 4: Generate unique template name per operator
                  const uniqueTemplateName =
                    template.name ||
                    `${campaignName}_whatsapp_${operatorMappingId}`;

                  console.log(
                    `🔍 [WHATSAPP TEMPLATE DEBUG] Operator ${operatorMappingId}:`,
                    {
                      templateName: template.name,
                      components: template.components,
                      bodyText: template.components?.find(
                        (c: any) => c.type === "BODY",
                      )?.text,
                      rawChannelBodyText:
                        ch.translated_template?.template?.components?.find(
                          (c: any) => c.type === "BODY",
                        )?.text,
                    },
                  );

                  // PATCH: Get these IDs from translatedTemplate
                  const phone_id = translatedTemplate.phone_id || "";
                  const template_id = translatedTemplate.template_id || "";
                  const channel_config_id =
                    translatedTemplate.channel_config_id ||
                    ch.channel_config_id ||
                    "";

                  isolatedTemplate = {
                    name: String(uniqueTemplateName),
                    category: String(template.category || "MARKETING"),
                    language: String(template.language || "en"),
                    components: JSON.parse(
                      JSON.stringify(template.components || []),
                    ), // Use actual components from this template
                    Base64: uniqueBase64,
                    media_config: uniqueMediaConfig,
                    qr: Array.isArray(translatedTemplate.qr)
                      ? translatedTemplate.qr
                      : [],
                    template: {
                      name: String(template.name || uniqueTemplateName),
                      category: String(template.category || "MARKETING"),
                      language: String(template.language || "en"),
                      components: JSON.parse(
                        JSON.stringify(template.components || []),
                      ), // Use actual components
                    },
                    translated_template: {
                      ...translatedTemplate,
                      from_country_iso: String(
                        channelCopy.from_country_iso || "",
                      ),
                      to_country_iso: String(channelCopy.to_country_iso || ""),
                      media_config: uniqueMediaConfig
                        ? JSON.parse(JSON.stringify(uniqueMediaConfig))
                        : null,
                      template: {
                        name: String(template.name || uniqueTemplateName),
                        category: String(template.category || "MARKETING"),
                        language: String(template.language || "en"),
                        components: JSON.parse(
                          JSON.stringify(template.components || []),
                        ), // Use actual components
                      },
                    },
                    phone_id,
                    template_id,
                    channel_config_id,
                    _operator_mapping_id: Number(operatorMappingId),
                    _from_country_iso: String(fromCountryISO),
                    _to_country_iso: String(channelCopy.to_country_iso || ""),
                    _unique_template_key: `wa_template_${operatorMappingId}_${fromCountryISO}`,
                    _baseAllocationName: `${campaignName}_whatsapp_${operatorMappingId}`,
                  };

                  console.log("Storing WhatsApp template with IDs:", {
                    phone_id,
                    template_id,
                    channel_config_id,
                    name: isolatedTemplate.name,
                    path: `${channelName}/${countryName}/${operatorMappingId}`,
                  });

                  console.log(
                    `✅ [WHATSAPP BUILT] Operator Mapping ${operatorMappingId}:`,
                    {
                      operatorMappingId: isolatedTemplate._operator_mapping_id,
                      fromCountry: isolatedTemplate._from_country_iso,
                      templateName: isolatedTemplate.name,
                      bodyText: isolatedTemplate.components?.find(
                        (c: any) => c.type === "BODY",
                      )?.text,
                      uniqueTemplateKey: isolatedTemplate._unique_template_key,
                    },
                  );
                } else if (channelId === 2) {
                  // SMS
                  console.log(
                    `🔵 [SMS] Processing operator mapping ${operatorMappingId} (${fromCountryISO})`,
                  );

                  // 🔥 FIX 5: Deep clone to prevent reference sharing
                  const channelCopy = JSON.parse(JSON.stringify(ch));
                  const translatedTemplate =
                    channelCopy.translated_template || {};
                  const template = translatedTemplate.template || {};

                  // 🔥 FIX 6: Use the actual message from this specific template
                  const smsMessage = String(
                    template.message || translatedTemplate.message || "",
                  );

                  console.log(
                    `📱 [SMS MESSAGE] Operator Mapping ${operatorMappingId}:`,
                    {
                      message: smsMessage,
                      messageLength: smsMessage.length,
                      fromCountry: fromCountryISO,
                      templateName: template.name,
                    },
                  );

                  // 🔥 FIX 7: Generate unique template name per operator
                  const uniqueTemplateName =
                    template.name || `${campaignName}_sms_${operatorMappingId}`;

                  console.log(
                    `🔍 [SMS TEMPLATE DEBUG] Operator ${operatorMappingId}:`,
                    {
                      templateName: template.name,
                      message: template.message,
                      translatedMessage: translatedTemplate.message,
                      rawChannelMessage:
                        ch.translated_template?.template?.message,
                    },
                  );

                  isolatedTemplate = {
                    message: smsMessage, // Use actual message from this template
                    template: {
                      name: String(uniqueTemplateName),
                      message: smsMessage, // Use actual message
                    },
                    api_type: String(translatedTemplate.api_type || "smpp"),
                    api_vendor: String(translatedTemplate.api_vendor || ""),
                    short_code: String(translatedTemplate.short_code || ""),
                    part_count: String(translatedTemplate.part_count || "1"),
                    _operator_mapping_id: Number(operatorMappingId), // 🔥 FIX 8: Use correct operatorMappingId
                    _from_country_iso: String(fromCountryISO),
                    _to_country_iso: String(channelCopy.to_country_iso || ""),
                    _unique_template_key: `sms_template_${operatorMappingId}_${fromCountryISO}`,
                    name: `${campaignName}_${operatorMappingId}`,
                    _baseAllocationName: `${campaignName}_${operatorMappingId}`,
                  };

                  console.log(
                    `✅ [SMS BUILT] Operator Mapping ${operatorMappingId}:`,
                    {
                      operatorMappingId: isolatedTemplate._operator_mapping_id,
                      fromCountry: isolatedTemplate._from_country_iso,
                      message: isolatedTemplate.message,
                      messageLength: isolatedTemplate.message?.length,
                      uniqueTemplateKey: isolatedTemplate._unique_template_key,
                    },
                  );
                }

                // 🔥 FIXED: Store template using operator_mapping_id as key
                console.log(
                  `🗄️ [STORAGE] Storing template with key ${operatorMappingId}:`,
                  {
                    path: `${channelName}/${countryName}/${operatorMappingId}`,
                    type: channelId === 1 ? "WhatsApp" : "SMS",
                  },
                );

                finalOperatorTemplates[channelName][countryName][
                  operatorMappingId
                ] = isolatedTemplate;

                // Verify storage
                const storedTemplate =
                  finalOperatorTemplates[channelName][countryName][
                    operatorMappingId
                  ];
                console.log(
                  `✅ [STORAGE VERIFIED] Template stored for key ${operatorMappingId}:`,
                  {
                    hasTemplate: !!storedTemplate,
                    templateOperatorId: storedTemplate?._operator_mapping_id,
                    templateFromCountry: storedTemplate?._from_country_iso,
                    uniqueKey: storedTemplate?._unique_template_key,
                  },
                );
              });
            }
          });
        }

        // Log final structure for debugging
        console.log("\n🏁 FINAL STRUCTURES:");
        console.log("📊 Allocations:", Object.keys(finalAllocations));
        console.log(
          "👥 Selected Operators:",
          Object.keys(finalSelectedOperators),
        );
        console.log("📄 Templates:", Object.keys(finalOperatorTemplates));

        // Detailed template logging
        Object.entries(finalOperatorTemplates).forEach(
          ([channel, countries]) => {
            console.log(`\n📱 Channel: ${channel}`);
            Object.entries(countries).forEach(([country, operators]) => {
              console.log(`  🌍 Country: ${country}`);
              Object.entries(operators).forEach(
                ([opMappingId, template]: [string, any]) => {
                  if (channel === "WhatsApp") {
                    console.log(
                      `    👤 Key ${opMappingId}: Template="${
                        template.name
                      }", Body="${
                        template.components?.find((c: any) => c.type === "BODY")
                          ?.text
                      }", UniqueKey=${template._unique_template_key}`,
                    );
                  } else {
                    console.log(
                      `    👤 Key ${opMappingId}: Message="${(
                        template.message || ""
                      ).substring(0, 50)}...", UniqueKey=${
                        template._unique_template_key
                      }`,
                    );
                  }
                },
              );
            });
          },
        );

        // Set all state at once
        setAllocations(finalAllocations);
        debugger;
        // ✅ FIX: Set editedAllocations calculated from total_traveller_count per operator mapping
        const finalEditedAllocations: Record<
          string,
          Record<string, Record<string, number>>
        > = {};

        // First pass: collect total travellers per channel+country
        const totalPerCountry: Record<string, Record<string, number>> = {};

        if (Array.isArray(d.operators)) {
          d.operators.forEach((operator: any) => {
            if (Array.isArray(operator.channels)) {
              operator.channels.forEach((ch: any) => {
                const channelName = channelIdToName(Number(ch.channel_id));
                const fromCountryISO = (
                  ch.from_country_iso || ""
                ).toUpperCase();
                const countryName =
                  isoToCountryNameMap[fromCountryISO] ||
                  fromCountryISO ||
                  "Unknown";
                const travellerCount = ch.total_traveller_count || 0;

                if (!totalPerCountry[channelName])
                  totalPerCountry[channelName] = {};
                totalPerCountry[channelName][countryName] =
                  (totalPerCountry[channelName][countryName] || 0) +
                  travellerCount;
              });
            }
          });
        }

        // Second pass: calculate allocation % from traveller count ratio
        if (Array.isArray(d.operators)) {
          d.operators.forEach((operator: any) => {
            if (Array.isArray(operator.channels)) {
              operator.channels.forEach((ch: any) => {
                const channelName = channelIdToName(Number(ch.channel_id));
                const fromCountryISO = (
                  ch.from_country_iso || ""
                ).toUpperCase();
                const countryName =
                  isoToCountryNameMap[fromCountryISO] ||
                  fromCountryISO ||
                  "Unknown";
                const operatorMappingId = ch.operator_mapping_id;
                const travellerCount = ch.total_traveller_count || 0;
                const totalTravellers =
                  totalPerCountry[channelName]?.[countryName] || 1;

                const allocation = Math.round(
                  (travellerCount / totalTravellers) * 100,
                );

                if (!finalEditedAllocations[channelName])
                  finalEditedAllocations[channelName] = {};
                if (!finalEditedAllocations[channelName][countryName])
                  finalEditedAllocations[channelName][countryName] = {};

                finalEditedAllocations[channelName][countryName][
                  operatorMappingId.toString()
                ] = allocation;
              });
            }
          });
        }

        setEditedAllocations(finalEditedAllocations);
        setSelectedOperators(finalSelectedOperators);
        console.log("🚀 [Final Operator Templates]:", finalOperatorTemplates);
        setOperatorTemplates(finalOperatorTemplates);

        // Fetch wm template name if wm_enable is true
        // if (d.wm_template?.wm_enable === true && d.wm_template?.wm_template_id) {
        //   try {
        //     const tmplRes = await axios.get(
        //       `${adminUrl}/GetWmTemplateIdByChannelConfigId?channel_config_id=${d.wm_template.wm_template_id}`
        //     );
        //     if (tmplRes.data.status === "Success" && tmplRes.data.data?.length > 0) {
        //       const templateName = tmplRes.data.data[0].template_name;
        //       setSelectedOperators(prev => {
        //         const updated = { ...prev };
        //         Object.keys(updated).forEach(channel => {
        //           Object.keys(updated[channel] || {}).forEach(country => {
        //             updated[channel][country] = updated[channel][country].map(op => ({
        //               ...op,
        //               wm_template_name: templateName,
        //             }));
        //           });
        //         });
        //         return updated;
        //       });
        //     }
        //   } catch (e) {
        //     console.error("Failed to fetch wm template name", e);
        //   }
        // }

        // Set active channel and first operator
        const firstChannel = uniqueChannels[0]?.channel_name;
        if (firstChannel) {
          setActiveChannel(firstChannel);
          console.log(`🎯 Set active channel: ${firstChannel}`);

          // Find first operator to set as selected
          const channelOps = finalSelectedOperators[firstChannel];
          if (channelOps) {
            const firstCountry = Object.keys(channelOps)[0];
            if (firstCountry && channelOps[firstCountry].length > 0) {
              const firstOp = channelOps[firstCountry][0];
              const newKey = {
                channel: firstChannel,
                country: firstCountry,
                operatorId: firstOp.id, // This will be the operator_mapping_id
                label: firstOp.label,
              };
              setSelectedOperatorKey(newKey);
              console.log(`🎯 Set initial selected operator:`, newKey);
            }
          }
        }

        // Set other campaign details (keep existing code unchanged)
        setToCountryTimezone(d.selected_to_country_timezone ?? "");
        setFromCountryIso(fromIso);
        debugger;
        setToCountryIso(toIso);
        setRawStartDate(d.start_date ? d.start_date.substring(0, 10) : "");
        setRawStartTime(d.start_time || "");
        setRawEndDate(d.end_date ? d.end_date.substring(0, 10) : "");
        setRawEndTime(d.end_time || "");

        // TZ id like "Asia/Dubai" (drop the "(UTC+...)" suffix if present)
        const cleanedTimezone = (d.selected_to_country_timezone || "UTC").split(
          " (",
        )[0];

        // helpers
        const dateOnly = (iso?: string) =>
          iso ? new Date(iso).toISOString().slice(0, 10) : ""; // "YYYY-MM-DD"
        const hhmm = (t?: string) => (t ? t.slice(0, 5) : ""); // "HH:mm"

        // ⬇️ pass only date parts to the converter
        const { localStartDate, localStartTime, localEndDate, localEndTime } =
          revertCampaignWindowFromUTC(
            dateOnly(d.start_date),
            d.start_time || "00:00:00",
            dateOnly(d.end_date),
            d.end_time || "00:00:00",
            cleanedTimezone,
          );

        // ⬇️ normalize for <input type="date"> / <input type="time">
        setStartDate((localStartDate || "").slice(0, 10)); // "YYYY-MM-DD"
        setEndDate((localEndDate || "").slice(0, 10)); // "YYYY-MM-DD"
        setStartTime(hhmm(localStartTime)); // "HH:mm"
        setEndTime(hhmm(localEndTime)); // "HH:mm"

        let travellerData;
        try {
          travellerData =
            typeof d.total_traveller_count === "string"
              ? JSON.parse(d.total_traveller_count)
              : d.total_traveller_count;
          setTotalTravellerCount(travellerData ?? null);
        } catch {
          setTotalTravellerCount(null);
        }

        setPremiumNumber(Boolean(d.premium_number));
        setDuplicateMsg(Boolean(d.duplicate_msg));
        setAllocationType(d.allocation_type ?? "");
        // setRoamingAge(d.roaming_age ? String(d.roaming_age) : "");
        // ---------------------
        // Extract unique from_country_iso from operators -> channels
        // ---------------------
        // Initialize empty object
        let roamingDaysByCountry: Record<string, any> = {};

        // Loop through all operators & channels
        (d.operators || []).forEach((operator: any) => {
          (operator.channels || []).forEach((channel: any) => {
            const iso = (channel.from_country_iso || "").toUpperCase();
            if (!iso) return;

            // Each channel has its own roaming_age
            const ra = channel.roaming_age || null;

            // Assign or overwrite for the country
            if (ra) {
              roamingDaysByCountry[iso] = {
                condition_a: {
                  value: ra.condition_a?.value ?? null,
                  con_operator: ra.condition_a?.con_operator ?? null,
                },
                condition_b: {
                  value: ra.condition_b?.value ?? null,
                  con_operator: ra.condition_b?.con_operator ?? null,
                },
              };
            }
          });
        });

        // Parse audience_config JSON
        let parsedAudienceConfig: any = null;
        if (d.audience_config) {
          try {
            parsedAudienceConfig =
              typeof d.audience_config === "string"
                ? JSON.parse(d.audience_config)
                : d.audience_config;
          } catch (error) {
            console.error("Error parsing audience_config:", error);
          }
        }

        // Set audience type and selected campaign name
        if (parsedAudienceConfig) {
          setAudienceType(parsedAudienceConfig.type || "");
          setSelectedCampaignName(d.linked_campaign_name || "");
          setAudienceConfig(parsedAudienceConfig);
        } else {
          setAudienceType("");
          setSelectedCampaignName("");
          setAudienceConfig(null);
        }

        console.log("Audience Config:", {
          audienceType: parsedAudienceConfig?.type,
          linkedCampaignName: d.linked_campaign_name,
          rawConfig: parsedAudienceConfig,
        });

        // Build fromCountryISOs { "Ghana": "GH", "Nigeria": "NG" }
        const fromCountryISOs = (d.from_country_iso || []).reduce(
          (acc: any, iso: string) => {
            const name =
              isoToCountryNameMap[iso.toUpperCase()] || iso.toUpperCase();
            acc[name] = iso.toUpperCase();
            return acc;
          },
          {},
        );

        // Build UI-friendly fromCountries
        const fromCountries = (d.from_country_iso || []).map(
          (iso: string) =>
            isoToCountryNameMap[iso.toUpperCase()] || iso.toUpperCase(),
        );

        // Set into state
        setRoamingDaysByCountry(roamingDaysByCountry);
        setFromCountryISOs(fromCountryISOs);
        setFromCountries(fromCountries);

        console.log("fromCountries", fromCountries);
        console.log("fromCountryISOs", fromCountryISOs);
        console.log("roamingDaysByCountry", roamingDaysByCountry);

        setUserAccountId(d.user_account_id ?? null);
        setAdvertiserWorkspaceId(d.advertiser_workspace_id ?? null);
        setCategoryId(d.category_id ?? null);
        setCategorySubId(d.category_sub_id ?? null);
      }
    } catch (err) {
      console.error("❌ Error in fetchAdminCampaignDetails:", err);
      toast.toast({
        title: "Error",
        description: "Failed to fetch admin campaign details",
      });
    }
  };

  const handleCheckStatus = async () => {
    try {
      const response = await axios.get(`${adminUrl}/CheckCampaignStatus`, {
        params: { campaignId },
      });

      let rawStatus = response.data?.campaignStatus;

      // Always parse if string starts with {
      if (typeof rawStatus === "string" && rawStatus.trim().startsWith("{")) {
        try {
          rawStatus = JSON.parse(rawStatus);
        } catch {
          // fallback
        }
      }

      if (rawStatus == null) {
        setCampaignStatus("unknown");
        return;
      }

      let statusStr: string;
      let partnerId: number;

      if (typeof rawStatus === "string") {
        statusStr = rawStatus; // "pending" or similar
      } else {
        statusStr = rawStatus.status ?? "unknown";
        partnerId = rawStatus.partner_id;
        if (statusStr === "rejected" && partnerId) {
          setRejectedWorkspaceId(partnerId);
        }
      }

      setCampaignStatus(statusStr);
      return statusStr;
    } catch (err) {
      setCampaignStatus("error");
    }
  };

  const handleCheckDeletedStatus = async () => {
    try {
      const response = await axios.get(`${adminUrl}/GetCampaignDeletedStatus`, {
        params: { campaignId },
      });

      const isDeleted = response.data?.isDeleted ?? false;

      setIsDeleted(isDeleted);

      return isDeleted;
    } catch (error) {
      console.error("Error checking deleted status:", error);

      setIsDeleted(false);

      return false;
    }
  };

  const handleApproveNotification = async (campaignId: number) => {
    try {
      const payload = {
        workspaceId: campaignWorkspaceId || 0,
        campaignId,
        campaignName,
        statusMark: "Unread",
        notificationType: "AdminCampaignApprove",
        notificationData: {
          Message: `Admin approved your campaign- ${campaignName}`,
        },
        role: "Advertiser",
      };

      console.log("Notification payload:", payload);

      const response = await axios.post(
        `${apiUrlAdvAcc}/CreateNotification`,
        payload,
      );

      if (response.data.status !== "Success") {
        throw new Error("Notification API failed.");
      }
    } catch (error) {
      console.error("Notification error:", error);
      toast.toast({
        title: "Notification Error",
        description: "Campaign approved but notification could not be sent.",
      });
    }
  };

  useEffect(() => {
    if (apiUrlAdvAcc) {
      const fetchData = async () => {
        try {
          await Promise.all([
            //campaignId ? loadCampaignList(campaignId) : Promise.resolve(),
            campaignId ? getAdminComment(campaignId) : Promise.resolve(),
          ]);
        } catch (error) {
          toast.toast({
            title: "Error",
            description: "Something went wrong, Please try again later",
          });
        }
      };
      fetchData();
    }
  }, [apiUrlAdvAcc]);

  const getAdminComment = async (campaignId: number) => {
    try {
      const response = await axios.get(
        `${apiUrlAdvAcc}/GetNotificationsForAdmin`,
      );

      const campaignNotifications: Notification[] = response.data.notifications
        .filter((n: any) => n.role?.toLowerCase() === "admin") // 🔹 Changed to admin
        .map((campaign: any) => {
          const rawData = campaign.notificationData || {};
          const normalizedData: Record<string, string> = {};
          Object.keys(rawData).forEach((key) => {
            const trimmedKey = key.trim();
            normalizedData[trimmedKey] = rawData[key];
          });

          return {
            id: campaign.id,
            campaignId: campaign.campaignId,
            statusMark: campaign.statusMark,
            notificationType: campaign.notificationType,
            createdAt: campaign.createdAt,
            notificationData: normalizedData,
            role: campaign.role,
          };
        });

      const matched = campaignNotifications.find(
        (n) => n.campaignId === campaignId && n.notificationData?.Comment,
      );

      setCampaignComment(matched?.notificationData?.Comment ?? null);
    } catch (error) {
      console.error("Error fetching admin comment:", error);
    }
  };

  const handleRejectAndCommentNotification = async (campaignId: number) => {
    try {
      const payload = {
        workspaceId: campaignWorkspaceId || 0,

        campaignId: campaignId,

        campaignName,

        statusMark: "Unread",

        notificationType: "AdminCampaignRejectComment",

        notificationData: {
          Message: `Admin rejected and commented on your campaign – ${campaignName}`,

          Comment: rejectComment || "", // Include the comment text
        },

        role: "Advertiser",
      };

      console.log("Combined Notification payload:", payload);

      const response = await axios.post(
        `${apiUrlAdvAcc}/CreateNotification`,

        payload,
      );

      if (response.data.status === "Success") {
        toast.toast({
          title: "Success",

          description: "Rejection and comment notification sent successfully.",
        });

        setTimeout(() => {
          setIsLoading(false);

          navigate("/adminNavbar/campaigns");
        }, 800);
      } else {
        throw new Error("Notification API failed.");
      }
    } catch (error) {
      console.error("Notification error:", error);

      toast.toast({
        title: "Notification Error",

        description: "Could not send rejection and comment notification.",
      });
    }
  };

  const triggerN8NWebhook = async (
    mappingIds: number[],
    isApproved: boolean,
  ) => {
    try {
      const payload = {
        operator_mapping_id: mappingIds,
        campaignID: campaignId,
        campaignName: campaignName,
        advertiserID: userAccountId?.toString(),
        workspaceID: advertiserWorkspaceId?.toString(),
        wrokspaceName: adminsworkspaceName,
        request_type: isApproved
          ? "admin_approval_campaign"
          : "admin_reject_campaign",
        comments: isApproved ? "Please approve the campaign" : rejectComment,
        advertiser_email: advertiserEmail,
        channel_id: [""],
        channel_type: [""],
      };

      const response = await axios.post(
        `${adminUrl}/N8NAdminCampaignApprovalandRejectionWebhook`,
        payload,
      );

      if (response.status !== 200) {
        console.error("❌ Webhook Trigger Failed");
      } else {
        console.log("✅ Webhook Triggered Successfully");
      }
    } catch (error) {
      console.error("❗ Error in triggerN8NWebhook", error);
    }
  };

  // Insert operator-style audit for auto-approvals (same payload as Operator UI)
  const insertAutoApprovalAudit = async (operatorWorkspaceId?: number) => {
    try {
      const payload = {
        accountId: userAccountId, // same shape as Operator UI
        campaignId: campaignId,
        operatorId: operatorWorkspaceId,
        status: "approved",
        approver: "Operator", // match Operator UI
        timestamp: new Date().toISOString(),
        rejectComment: "",
      };

      await axios.post(`${operatorUrl}/UpdateOperatorApprovalStatus`, payload);
    } catch (err) {
      console.error("❌ Failed to insert auto-approval operator audit:", err);
    }
  };

  const triggerOperatorAutoApprovalWebhook = async (
    operatorMappingId: number,
    operatorWorkspaceId?: number,
  ) => {
    try {
      debugger;
      const payload = {
        operator_mapping_id: [operatorMappingId],
        campaignID: campaignId,
        campaignName: campaignName,
        advertiserID: userAccountId?.toString(),
        workspaceID: operatorWorkspaceId?.toString() ?? "", // guard
        wrokspaceName: "",
        request_type: "operator_approval_campaign",
        comments: "Auto-approved operator - please proceed with campaign",
        advertiser_email: advertiserEmail,
        channel_id: [""],
        channel_type: [""],
      };

      const response = await axios.post(
        `${operatorUrl}/N8NOperatorCampaignApprovalandRejectionWebhook`,
        payload,
      );

      if (response.status !== 200) {
        console.error(
          `❌ Auto-approval webhook failed for operator ${operatorMappingId}`,
        );
      } else {
        console.log(
          `✅ Auto-approval webhook triggered for operator ${operatorMappingId}`,
        );
      }
    } catch (error) {
      console.error(
        `❗ Error in auto-approval webhook for operator ${operatorMappingId}:`,
        error,
      );
    }
  };

  const handleReject = async () => {
    const payload = {
      userAccountId: accountId,
      campaignId: campaignId,
      advertiserWorkspaceId: workspaceId,
      action: "admin rejected",
      actionPage: "Admin Campaign Approval",
      status: "rejected",
      beforeData: null,
      afterData: null,
      ipAddress: null,
      comments: rejectComment,
    };

    let apiResponseStatus = "Failed"; // Track status for audit
    let apiResponseMessage = "";
    // let auditStatus = "Failed";
    try {
      setIsLoading(true);

      setCampaignStatus("admin rejected");
      const response = await axios.post(
        `${adminUrl}/AdminRejectCampaign`,
        payload,
      );

      if (response.status === 200) {
        await triggerN8NWebhook([], false);
        await handleRejectAndCommentNotification(campaignId);
        console.log("✅ Success:", response.data.message);
        setRejectComment("");
        apiResponseStatus = "Success";
        apiResponseMessage = "Campaign rejected successfully.";
        toast.toast({
          title: "Success",
          description: "Campaign Rejected Successfully.",
        });
        setTimeout(() => {
          navigate("/adminNavbar/campaigns");
        }, 2000);
      } else {
        apiResponseStatus = "failed";
        apiResponseMessage = "Campaign rejection failed.";
        toast.toast({
          title: "Error",
          description: response.data.Status_Description || "Approval failed.",
        });
      }
    } catch (error: any) {
      apiResponseStatus = "failed";
      apiResponseMessage = "Campaign rejection failed.";
      toast.toast({
        title: "Error",
        description:
          error?.response?.data?.Status_Description ||
          error.message ||
          "Unknown error.",
      });
    } finally {
      setIsLoading(false);
      try {
        // ✅ Prepare AfterData for Audit
        const auditAfterData = {
          campaignId,
          action: "Campaign Rejected by Admin",
          timestamp: new Date().toISOString(),
          comments: rejectComment,
        };

        const auditPayload = {
          UserAccountId: CurrentAID, // ✅ match backend parameter
          CampaignId: campaignId,
          AdvertiserWorkspaceId: workspaceId,
          Action: "Admin Campaign Rejection",
          ActionPage: "Admin Campaign Approval",
          Status: apiResponseStatus,
          BeforeData: null, // server will fetch automatically
          AfterData: JSON.stringify(auditAfterData),
          IpAddress: null, // auto-filled in backend
          Comments: apiResponseMessage,
        };

        console.log("📝 Sending Audit Payload:", auditPayload);

        const auditResponse = await axios.post(
          `${apiUrlAdvAcc}/InsertOverAllAuditHistory`,
          auditPayload,
        );

        if (auditResponse?.data?.Status === "Success") {
          console.log("✅ Audit inserted successfully.");
        } else {
          console.warn("⚠️ Audit insert failed:", auditResponse?.data);
        }
      } catch (auditError: any) {
        console.error("❌ Failed to insert audit:", auditError);
      }
    }
  };

  const handleSandboxReject = async () => {
    try {
      setIsLoading(true);
      const response = await axios.put(
        `${adminUrl}/RejectSandboxCampaign?campaignId=${campaignId}`,
      );

      if (response.data.status === "Success") {
        toast.toast({
          title: "Success",
          description: "Campaign rejected successfully.",
        });
        setTimeout(() => {
          navigate("/adminNavbar/campaigns");
        }, 1000);
      } else {
        toast.toast({
          title: "Error",
          description: response.data.message || "Rejection failed.",
        });
      }
    } catch (error: any) {
      toast.toast({
        title: "Error",
        description:
          error?.response?.data?.message || "Failed to reject campaign.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fixed handleApprove function with correct api_type and api_vendor parsing
  const handleApprove = async () => {
    const campaignDetailsPayload = {
      campaign_id: campaignId,
      category_id: categoryId,
      category_sub_id: categorySubId,
      user_account_id: userAccountId,
      advertiser_workspace_id: advertiserWorkspaceId,
      admin_workspace_id: admin_workspace_id,
      name: campaignName,
      channels: availableChannels.map((ch) => ch.channel_name),
      reachFrom: fromCountryIso,
      reachTravelTo: [toCountryIso],
      startDate: rawStartDate,
      endDate: rawEndDate,
      startTime: rawStartTime,
      endTime: rawEndTime,
      allocationType,
      // roamingDays: roamingAge,
      isDuplicatePrevented: duplicateMsg,
      isPremiumOnly: premiumNumber,
      campaignStatusFromApi: campaignStatusFromApi,
      campaignApprovalByAdmin: "approved",
      audience_config: audienceConfig,
    };

    const extTemplateCounters: Record<string, number> = {
      Whatsapp: 1,
      WhatsApp: 1,
      SMS: 1,
    };

    const countriesFlatList: string[] = [];
    Object.keys(selectedOperators).forEach((channel) =>
      Object.keys(selectedOperators[channel] || {}).forEach((country) => {
        if (!countriesFlatList.includes(country))
          countriesFlatList.push(country);
      }),
    );

    const allOperatorAllocations: any[] = [];
    const autoApprovalInfo: Array<{
      operatorId?: number;
      workspaceId?: number;
      allocation: any;
      mappingId?: number; // Add this to store the mapping ID when we get it
    }> = []; // Track auto-approval operators

    Object.keys(selectedOperators).forEach((channel) => {
      const channel_id = channelNameToId[channel] ?? null;
      const templateObj = templates.find((t) => {
        const chName = availableChannels.find(
          (c) => c.channel_id === t.channel_id,
        )?.channel_name;
        return chName?.toLowerCase() === channel.toLowerCase();
      });
      const template_id = templateObj?.template_id ?? null;

      const countryList = allocations?.[channel] ?? [];
      Object.keys(selectedOperators[channel] || {}).forEach((country) => {
        const countryIso = (countryToIso[country] ?? "").toUpperCase();
        const country_iso = countryIso.toLowerCase();

        // 🔥 FIXED: Parse from roamingAge state (which has the raw JSON string)
        let countrySpecificRoamingAge: any = null;

        try {
          let roamingAgeObj: Record<string, any> = {};
          debugger;
          // If roamingDaysByCountry is a string → parse JSON
          if (typeof roamingDaysByCountry === "string") {
            roamingAgeObj = JSON.parse(roamingDaysByCountry) as Record<
              string,
              any
            >;
            console.log("📦 Parsed roamingAge:", roamingAgeObj);
          }

          // If roamingDaysByCountry is already an object → use directly
          else if (
            roamingDaysByCountry &&
            typeof roamingDaysByCountry === "object"
          ) {
            roamingAgeObj = roamingDaysByCountry as Record<string, any>;
          }

          // Get the roaming age for this specific country ISO (lowercase)
          if (roamingAgeObj && typeof roamingAgeObj === "object") {
            const isoKey = country_iso.toUpperCase(); // 'bj', 'fr', etc.
            countrySpecificRoamingAge = roamingAgeObj[isoKey] || null;

            console.log(
              `🔍 Roaming age for ${country} (${isoKey}):`,
              countrySpecificRoamingAge,
            );
          }
        } catch (error) {
          console.error(
            "❌ Error parsing roaming age for country:",
            country_iso,
            error,
          );
          countrySpecificRoamingAge = null;
        }

        // Convert to JSON string for backend
        const roamingAgeJson = countrySpecificRoamingAge
          ? JSON.stringify(countrySpecificRoamingAge)
          : null;

        console.log(
          `✅ Final roaming_age JSON for ${country}:`,
          roamingAgeJson,
        );
        debugger;
        const countryCode = isoToCountryCode[countryIso] ?? "";
        const toIso = (toCountryIso ?? "").toUpperCase();
        const toCode = isoToCountryCode[toIso] ?? "";

        const to_country_iso = toIso.toLowerCase();

        const countryInfo = countryList.find((c) => c.country === country);
        const countryTravellerCount = countryInfo?.travellers ?? 0;
        const operatorList = selectedOperators[channel][country] || [];

        operatorList.forEach((operator, opIdx) => {
          const operator_id = operator.partner_id;
          const label =
            operator.label ?? operator.channel_config_name ?? "Operator";
          const channel_config_id = operator.channel_config_id;

          let api_type = "";
          let api_vendor = "";
          let channelConfigObj: any = {};

          if (operator.channel_config) {
            if (typeof operator.channel_config === "string") {
              try {
                channelConfigObj = JSON.parse(operator.channel_config);
              } catch {
                channelConfigObj = {};
              }
            } else if (
              typeof operator.channel_config === "object" &&
              operator.channel_config !== null
            ) {
              channelConfigObj = operator.channel_config;
            }

            api_type = channelConfigObj.api_type || "";
            api_vendor = channelConfigObj.api_vendor || "";
          }

          let part_count: number = 1;
          if (channel.toLowerCase() === "sms") {
            const smsTemplateObj =
              operatorTemplates?.[channel]?.[country]?.[operator.id] || {};
            let messageText = "";
            if (smsTemplateObj.message) {
              messageText = smsTemplateObj.message;
            } else if (
              smsTemplateObj.template &&
              smsTemplateObj.template.message
            ) {
              messageText = smsTemplateObj.template.message;
            }

            const info = getSmsPartsCount(messageText || "");
            part_count = typeof info === "number" ? info : info.parts;
          }

          let approvalFlowStatus: any[] = [
            {
              stage: "TravelAdmin",
              status: "approved",
              approver: "Admin",
              timestamp: new Date().toISOString(),
            },
            {
              stage: "Operator",
              status: operator.auto_approval === true ? "approved" : "pending",
              approver: operator.auto_approval === true ? "Auto Approval" : "",
              timestamp:
                operator.auto_approval === true ? new Date().toISOString() : "",
            },
            ...(channel_id === 1
              ? [
                  {
                    stage: "WhatsApp",
                    status: "pending",
                    approver: "",
                    timestamp: "",
                  },
                ]
              : []),
          ];

          const allocationPercent = (() => {
            const edited =
              editedAllocations[channel]?.[country]?.[operator.id.toString()];
            if (typeof edited === "number") return edited;
            const operatorCount = operatorList.length;
            if (operatorCount === 0) return 0;
            const base = Math.floor(100 / operatorCount);
            const extra = 100 - base * operatorCount;
            return base + (opIdx === 0 ? extra : 0);
          })();

          let allocatedTravellerCount;
          if (opIdx === operatorList.length - 1) {
            const allocatedSoFar = operatorList
              .slice(0, opIdx)
              .reduce((sum, op, idx) => {
                const percent = (() => {
                  const edited =
                    editedAllocations[channel]?.[country]?.[
                      operatorList[idx].id?.toString()
                    ];
                  if (typeof edited === "number") return edited;
                  const operatorCount = operatorList.length;
                  if (operatorCount === 0) return 0;
                  const base = Math.floor(100 / operatorCount);
                  const extra = 100 - base * operatorCount;
                  return base + (idx === 0 ? extra : 0);
                })();
                return (
                  sum + Math.round((countryTravellerCount * percent) / 100)
                );
              }, 0);
            allocatedTravellerCount = countryTravellerCount - allocatedSoFar;
          } else {
            allocatedTravellerCount = Math.round(
              (countryTravellerCount * allocationPercent) / 100,
            );
          }

          const templateName =
            templateObj?.template_name ||
            templateObj?.template_content?.name ||
            campaignName;

          const currentTemplate =
            operatorTemplates?.[channel]?.[country]?.[operator.id] || {};
          let translated_template: any = {};

          if (channel.toLowerCase() === "whatsapp") {
            translated_template = {
              channel_id: channel_id?.toString() || "1",
              channel_type: "Whatsapp",
              channel_config_id: channel_config_id?.toString() || "",
              part_count: part_count.toString(),
              campaign_name: campaignName,
              api_type: api_type,
              api_vendor: api_vendor,
              // phone_id: operator.phone_id,

              // AFTER:
              phone_id:
                audienceType === "Reuse Audience"
                  ? channelConfigObj?.config?.phone_id || ""
                  : operator.phone_id,
              template_id: template_id?.toString() || "",
              template: {
                name: templateName,
                language: currentTemplate.language || "en_US",
                category: currentTemplate.category || "MARKETING",
                message_send_ttl_seconds: operator.ttl_hours,
                components: (currentTemplate.components || []).map(
                  fixHeaderComponent,
                ),
              },
              media_config: currentTemplate.media_config || undefined,
              qr: currentTemplate.qr || [],
            };
          } else if (channel.toLowerCase() === "sms") {
            const currentTemplate =
              operatorTemplates?.[channel]?.[country]?.[operator.id] || {};

            let messageText = currentTemplate.message;
            if (
              !messageText &&
              currentTemplate.template &&
              currentTemplate.template.message
            ) {
              messageText = currentTemplate.template.message;
            }
            translated_template = {
              channel_id: channel_id?.toString() || "2",
              channel_type: "SMS",
              channel_config_id: channel_config_id?.toString() || "",
              part_count: part_count.toString(),
              campaign_name: campaignName,
              api_type: api_type,
              api_vendor: api_vendor,
              short_code: operator.short_code || "",
              template: {
                name:
                  templateObj?.template_name ||
                  templateObj?.template_content?.name ||
                  campaignName,
                message: messageText || "",
              },
            };
          }
          debugger;

          const operatorAllocation = {
            channel,
            channel_id,
            country,
            country_iso: country_iso,
            country_code: countryCode,
            to_country_iso: to_country_iso,
            to_country_code: toCode,
            operator_id,
            operator_label: label,
            allocation_percent: allocationPercent,
            total_message_count: allocatedTravellerCount,
            template_id: template_id,
            ext_template_id: String(extTemplateCounters[channel]),
            channel_config_id: channel_config_id,
            approval_status:
              channel.toLowerCase() === "sms" && operator.auto_approval === true
                ? "approved"
                : "pending",
            api_type,
            api_vendor,
            part_count,
            short_code: operator.short_code || "",
            roaming_age: roamingAgeJson,
            template: {
              ...currentTemplate,
            },
            translated_template: translated_template,
            approvalFlowStatus,
            wm_part_count: operator.welcomeMessage
              ? channel_id === 1
                ? 1
                : operator.wm_part_count
              : null,
            wm_template: {
              wm_enable: operator.welcomeMessage || false,
              wm_template_id: operator.wm_template_id || null,
              wm_variables: operator.wm_variables || null,
            },
          };

          allOperatorAllocations.push(operatorAllocation);

          // Track auto-approval operators (but don't process them yet)
          if (operator.auto_approval === true) {
            const operatorId =
              typeof operator_id === "number" ? operator_id : undefined;
            const wsId =
              typeof operator.partner_id === "number"
                ? operator.partner_id
                : undefined;

            if (operatorId && wsId) {
              autoApprovalInfo.push({
                operatorId,
                workspaceId: wsId,
                allocation: operatorAllocation,
              });
            } else {
              console.warn("Skipping auto-approval push due to missing ids", {
                operator_id,
                partner_id: operator.partner_id,
              });
            }
          }

          extTemplateCounters[channel] += 1;
        });
      });
    });

    console.log(
      "APPROVE PAYLOAD (will be split per operator):",
      JSON.stringify(
        {
          campaignDetails: campaignDetailsPayload,
          totalOperators: allOperatorAllocations.length,
          autoApprovalCount: autoApprovalInfo.length,
        },
        null,
        2,
      ),
    );

    try {
      setIsMappingLoading(true);
      const successfulMappingIds: number[] = [];
      const successfulWorkspaceIds: number[] = [];
      const autoApprovedMappingIds: number[] = [];
      const failedOperators: Array<{
        operator: string;
        channel: string;
        country: string;
        error: string;
      }> = [];

      console.log(
        `Starting approval process for ${allOperatorAllocations.length} operators`,
      );
      console.log(`Auto-approval operators: ${autoApprovalInfo.length}`);

      // Process all operators first (without auto-approval audit)
      for (let i = 0; i < allOperatorAllocations.length; i++) {
        const operatorAllocation = allOperatorAllocations[i];
        const autoApprovalOperator = autoApprovalInfo.find(
          (auto) => auto.operatorId === operatorAllocation.operator_id,
        );

        const individualPayload = {
          campaignDetails: campaignDetailsPayload,
          operatorAllocations: [operatorAllocation],
        };

        const payloadString = JSON.stringify(individualPayload);
        const payloadSizeBytes = new Blob([payloadString]).size;
        const payloadSizeKB = (payloadSizeBytes / 1024).toFixed(2);
        const payloadSizeMB = (payloadSizeBytes / (1024 * 1024)).toFixed(3);

        console.log(
          `Processing operator ${i + 1}/${allOperatorAllocations.length}: ${
            operatorAllocation.operator_label
          } (${operatorAllocation.channel}/${operatorAllocation.country})`,
        );
        console.log(
          `Payload size: ${payloadSizeBytes} bytes | ${payloadSizeKB} KB | ${payloadSizeMB} MB`,
        );

        if (autoApprovalOperator) {
          console.log(`This operator has auto-approval enabled`);
        }

        try {
          const response = await axios.post(
            `${adminUrl}/InsertCampaignOperatorMapping`,
            individualPayload,
          );

          const desc = String(
            response?.data?.status_Description ?? "",
          ).toLowerCase();
          if (
            response?.data?.status === "Error" &&
            desc.includes("already deleted")
          ) {
            setIsMappingLoading(false);
            toast.toast({
              title: "Campaign deleted",
              description: "This campaign is already deleted by advertiser.",
            });
            setTimeout(() => {
              navigate("/adminNavbar/campaigns");
            }, 2000);
            return; // exit the approve flow early
          }

          if (
            response.data.status === "Success" &&
            response.data.operatorMappingIds
          ) {
            successfulMappingIds.push(...response.data.operatorMappingIds);
            successfulWorkspaceIds.push(operatorAllocation.operator_id);
            console.log(
              `Success: ${operatorAllocation.operator_label} - Mapping ID: ${response.data.operatorMappingIds[0]}`,
            );

            // Store mapping ID for auto-approval operators (but don't process yet)
            if (autoApprovalOperator) {
              const mappingId = response.data.operatorMappingIds[0];
              autoApprovedMappingIds.push(mappingId);
              // Store the mapping ID for later processing
              autoApprovalOperator.mappingId = mappingId;
            }
          } else {
            console.error(
              `Failed: ${operatorAllocation.operator_label} - ${
                response.data.Status_Description || "Unknown error"
              }`,
            );
            failedOperators.push({
              operator: operatorAllocation.operator_label,
              channel: operatorAllocation.channel,
              country: operatorAllocation.country,
              error: response.data.Status_Description || "Unknown error",
            });
          }

          if (i < allOperatorAllocations.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 10));
          }
        } catch (error: any) {
          const serverDesc = String(
            error?.response?.data?.status_Description ?? "",
          ).toLowerCase();
          if (serverDesc.includes("already deleted")) {
            setIsMappingLoading(false);
            toast.toast({
              title: "Campaign deleted",
              description: "This campaign is already deleted by advertiser.",
            });

            setTimeout(() => {
              navigate("/adminNavbar/campaigns");
            }, 2000);
            return; // stop the approve flow
          }

          failedOperators.push({
            operator: operatorAllocation.operator_label,
            channel: operatorAllocation.channel,
            country: operatorAllocation.country,
            error:
              error?.response?.data?.Status_Description ||
              error.message ||
              "Network error",
          });
        }
      }

      // Show results
      if (successfulMappingIds.length > 0) {
        console.log(
          `Successfully processed ${successfulMappingIds.length} operators`,
        );
        console.log(
          `Auto-approved operators: ${autoApprovedMappingIds.length}`,
        );

        // Trigger webhook and notifications for successful operators
        if (successfulMappingIds.length > 0) {
          await triggerN8NWebhook(successfulMappingIds, true);
          await handleApproveNotification(campaignId);
        }

        if (failedOperators.length === 0) {
          toast.toast({
            title: "Success",
            description: `Campaign Approved Successfully.`,
          });
          axios
            .post(`${adminUrl}/NotifyOperatorsAfterMapping`, {
              campaignId,
              campaignName,
            })
            .then(() =>
              console.log(
                "Operator notifications inserted & published via ntfy.",
              ),
            )
            .catch((err) =>
              console.error(
                "NotifyOperatorsAfterMapping failed:",
                err?.response?.data || err.message,
              ),
            );

          try {
            console.log("📘 Inserting overall audit history...");

            // Construct AfterData JSON
            const afterData = {
              totalOperators: allOperatorAllocations.length,
              autoapprovaloperators: autoApprovalInfo.map((a) => a.workspaceId),
              successfulOperators: successfulWorkspaceIds,
              failedOperators: failedOperators.map((f) => ({
                operator: f.operator,
                channel: f.channel,
                country: f.country,
                error: f.error,
              })),
              campaignStatus:
                failedOperators.length === 0
                  ? "Approved Successfully"
                  : "Partial Approval / Some Failed",
              welcomeMessageDetails: allOperatorAllocations.map((op) => ({
                operator: op.operator_label,
                channel: op.channel,
                country: op.country,
                welcomeMessageEnabled: op.wm_template?.wm_enable === true,
                wm_template_id:
                  op.wm_template?.wm_enable === true
                    ? op.wm_template?.wm_template_id || null
                    : null, // SMS
              })),
            };

            const auditPayload = {
              UserAccountId: CurrentAID,
              CampaignId: campaignId,
              AdvertiserWorkspaceId: admin_workspace_id,
              Action: "Admin Campaign Approval",
              ActionPage: "Admin Campaign Approval",
              Status: failedOperators.length === 0 ? "Success" : "Failed",
              AfterData: JSON.stringify(afterData),
              Comments:
                failedOperators.length === 0
                  ? "Campaign approved successfully."
                  : "Campaign approval failed.",
            };

            console.log("Overall audit payload:", auditPayload);

            await axios.post(
              `${apiUrlAdvAcc}/InsertOverAllAuditHistory`,
              auditPayload,
            );

            console.log("✅ Overall audit inserted successfully");
          } catch (auditError: any) {
            console.error("❌ Failed to insert overall audit:", auditError);
          }

          // Process auto-approvals directly (no longer gated behind InsertAuditHistory)
          if (autoApprovalInfo.length > 0) {
            console.log(
              `🔥 Processing ${autoApprovalInfo.length} auto-approval operators...`,
            );

            for (const autoApprovalOperator of autoApprovalInfo) {
              if (autoApprovalOperator.mappingId) {
                console.log(
                  `🔥 Triggering auto-approval webhook for operator ${autoApprovalOperator.operatorId} with mapping ID: ${autoApprovalOperator.mappingId}`,
                );

                await triggerOperatorAutoApprovalWebhook(
                  autoApprovalOperator.mappingId,
                  autoApprovalOperator.workspaceId,
                );

                await insertAutoApprovalAudit(autoApprovalOperator.workspaceId);
              }
            }
            console.log("✅ All auto-approval processing completed");
          }
          // try {
          //   console.log("📘 Inserting overall audit history...");

          //   // Construct AfterData JSON
          //   const afterData = {
          //     totalOperators: allOperatorAllocations.length,
          //     autoapprovaloperators: autoApprovalInfo.map(a => a.workspaceId),
          //     successfulOperators: successfulWorkspaceIds,
          //     failedOperators: failedOperators.map(f => ({
          //       operator: f.operator,
          //       channel: f.channel,
          //       country: f.country,
          //       error: f.error
          //     })),
          //     campaignStatus:
          //       failedOperators.length === 0
          //         ? "Approved Successfully"
          //         : "Partial Approval / Some Failed"
          //   };

          //   const auditPayload = {
          //     UserAccountId: CurrentAID,
          //     CampaignId: campaignId,
          //     AdvertiserWorkspaceId: admin_workspace_id,
          //     Action: "Admin Approval",
          //     ActionPage: "Admin Campaign Approval",
          //     Status: failedOperators.length === 0 ? "Success" : "Failed",
          //     AfterData: JSON.stringify(afterData),
          //     Comments:
          //       failedOperators.length === 0
          //         ? "Campaign approved successfully."
          //         : "Campaign approval failed.",
          //   };

          //   console.log("Overall audit payload:", auditPayload);

          //   await axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, auditPayload);

          //   console.log("✅ Overall audit inserted successfully");
          // } catch (auditError: any) {
          //   console.error("❌ Failed to insert overall audit:", auditError);
          // }
        } else {
          toast.toast({
            title: "Partial Success",
            description: `${successfulMappingIds.length} operators approved, ${failedOperators.length} failed. Check console for details.`,
          });
          console.error("Failed operators:", failedOperators);
        }

        setTimeout(() => {
          navigate("/adminNavbar/campaigns");
        }, 2000);
      } else {
        toast.toast({
          title: "Error",
          description: `No operators could be processed. ${failedOperators.length} failures.`,
        });
        console.error("All operators failed:", failedOperators);
      }
    } catch (error: any) {
      console.error("Critical error in approval process:", error);
      toast.toast({
        title: "Critical Error",
        description:
          error?.message || "An unexpected error occurred during approval.",
      });
    } finally {
      setIsMappingLoading(false);
    }
  };

  const updateRejectedOperatorTemplates = async () => {
    setIsLoading(true);

    try {
      const operatorUpdates: Array<{
        operatorMappingId: number;
        translatedTemplate: any;
        approvalFlowStatus: any[];
        operator: any;
        channel: string;
        country: string;
      }> = [];

      // Build the list of operators to update
      // Object.entries(selectedOperators).forEach(([channel, countriesObj]) => {
      //   Object.entries(countriesObj).forEach(([country, operators]) => {
      //     operators.forEach(async (operator) => {
      for (const [channel, countriesObj] of Object.entries(selectedOperators)) {
        for (const [country, operators] of Object.entries(countriesObj)) {
          for (const operator of operators) {
            if (
              operator.operator_mapping_id &&
              (operator.status === "rejected" ||
                (campaignStatusForSandbox === "sandbox" &&
                  (operator.status?.toLowerCase() === "pending" ||
                    operator.status?.toLowerCase() === "rejected")))
            ) {
              const editedTemplate =
                operatorTemplates?.[channel]?.[country]?.[operator.id];

              if (editedTemplate) {
                let channelConfigObj: any = {};
                if (operator.channel_config) {
                  if (typeof operator.channel_config === "string") {
                    try {
                      channelConfigObj = JSON.parse(operator.channel_config);
                    } catch {
                      channelConfigObj = {};
                    }
                  } else if (
                    typeof operator.channel_config === "object" &&
                    operator.channel_config !== null
                  ) {
                    channelConfigObj = operator.channel_config;
                  }
                }

                // Build approvalFlowStatus array for this operator
                let approvalFlowStatus: any[] = [
                  {
                    stage: "TravelAdmin",
                    status: "approved",
                    approver: "Admin",
                    timestamp: new Date().toISOString(),
                  },
                  {
                    stage: "Operator",
                    status:
                      operator.auto_approval === true ? "approved" : "pending",
                    approver:
                      operator.auto_approval === true ? "Auto Approval" : "",
                    timestamp:
                      operator.auto_approval === true
                        ? new Date().toISOString()
                        : "",
                  },
                ];
                if (channel.toLowerCase() === "whatsapp") {
                  approvalFlowStatus.push({
                    stage: "WhatsApp",
                    status: "pending",
                    approver: "",
                    timestamp: "",
                  });
                }

                const channelId = channelNameToId[channel];

                // Get template_id from the edited template
                let template_id =
                  editedTemplate.template_id ||
                  editedTemplate.template?.template_id ||
                  editedTemplate.id ||
                  "";

                if (!template_id) {
                  console.error(
                    `⚠️ CRITICAL: No template_id found in editedTemplate for channel: ${channel}, operator: ${operator.id}`,
                  );
                  console.error(
                    "EditedTemplate structure:",
                    JSON.stringify(editedTemplate, null, 2),
                  );
                } else {
                  console.log(
                    `✅ Found template_id: ${template_id} for ${channel}/${country}/${operator.id}`,
                  );
                }

                let translatedTemplate: any = {};

                if (channel.toLowerCase() === "sms") {
                  const messageText =
                    editedTemplate.message ||
                    editedTemplate.template?.message ||
                    "";

                  const existingName =
                    editedTemplate.template?.name ||
                    editedTemplate.name ||
                    `${campaignName}_${channel.toLowerCase()}`;

                  const smsInfo = getSmsPartsCount(messageText || "");
                  const partCount = smsInfo?.parts ?? 1;

                  translatedTemplate = {
                    api_type: channelConfigObj.api_type || "smpp",
                    template: {
                      name: existingName,
                      message: messageText,
                    },
                    api_vendor:
                      channelConfigObj.api_vendor ||
                      operator.channel_config_name ||
                      "",
                    channel_id: channelId?.toString() || "2",
                    part_count: String(partCount),
                    short_code: operator.short_code || "",
                    channel_type: "SMS",
                    campaign_name: campaignName,
                    channel_config_id:
                      editedTemplate.channel_config_id?.toString() ||
                      editedTemplate.translated_template?.channel_config_id?.toString() ||
                      operator.channel_config_id?.toString() ||
                      "",
                  };
                } else if (channel.toLowerCase() === "whatsapp") {
                  // Extract header component and its format
                  let headerFormat = "";
                  let headerComp = null;
                  if (
                    editedTemplate.components &&
                    Array.isArray(editedTemplate.components)
                  ) {
                    headerComp = editedTemplate.components.find(
                      (c: any) => c.type === "HEADER",
                    );
                    headerFormat = headerComp?.format
                      ? headerComp.format.toUpperCase()
                      : "";
                  }

                  // Default empty media_config
                  let media_config = {
                    file_name: "",
                    file_type: "",
                    binary_data: "",
                    file_length: "",
                  };

                  // Attach latest media_config from template (if present)
                  if (
                    ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat) &&
                    editedTemplate.media_config &&
                    editedTemplate.media_config.binary_data
                  ) {
                    media_config = { ...editedTemplate.media_config };
                  }

                  const existingName =
                    editedTemplate.template?.name ||
                    editedTemplate.name ||
                    `${campaignName}_${channel.toLowerCase()}`;

                  const phone_id =
                    editedTemplate.phone_id ||
                    editedTemplate.template?.phone_id ||
                    editedTemplate.translated_template?.phone_id ||
                    "";

                  const configId = Number(operator.channel_config_id);

                  const ttlSeconds = await getTTLSeconds(Number(configId));

                  translatedTemplate = {
                    api_type: channelConfigObj.api_type || "custom",
                    template: {
                      name: existingName,
                      category: editedTemplate.category || "MARKETING",
                      language: editedTemplate.language || "en",
                      message_send_ttl_seconds: ttlSeconds,
                      components: (editedTemplate.components || []).map(
                        fixHeaderComponent,
                      ),
                    },
                    api_vendor:
                      channelConfigObj.api_vendor ||
                      operator.channel_config_name ||
                      "",
                    channel_id: channelId?.toString() || "1",
                    part_count: "1",
                    template_id: template_id,
                    phone_id,
                    channel_type: "Whatsapp",
                    campaign_name: campaignName,
                    channel_config_id:
                      editedTemplate.channel_config_id?.toString() ||
                      editedTemplate.translated_template?.channel_config_id?.toString() ||
                      operator.channel_config_id?.toString() ||
                      "",
                    media_config,
                    qr: editedTemplate.qr || [],
                  };
                }

                operatorUpdates.push({
                  operatorMappingId: operator.operator_mapping_id,
                  translatedTemplate,
                  approvalFlowStatus,
                  operator,
                  channel,
                  country,
                });
              }
            }
          }
        }
      }

      let apiStatus = "Failed";
      let afterDataMessage = "";
      const successfulUpdates: number[] = [];
      const failedUpdates: Array<{
        operatorMappingId: number;
        operator: string;
        channel: string;
        country: string;
        error: string;
      }> = [];

      try {
        if (operatorUpdates.length > 0) {
          console.log(
            `Starting update process for ${operatorUpdates.length} rejected operators`,
          );

          // Process each operator individually
          for (let i = 0; i < operatorUpdates.length; i++) {
            const update = operatorUpdates[i];

            // Create individual payload for this operator
            const individualPayload = {
              operatorMappingIds: [update.operatorMappingId],
              newTemplates: [update.translatedTemplate],
              ApprovalFlowStatuses: [update.approvalFlowStatus],
              campaignId: campaignId,
            };

            const payloadString = JSON.stringify(individualPayload);
            const payloadSizeBytes = new Blob([payloadString]).size;
            const payloadSizeKB = (payloadSizeBytes / 1024).toFixed(2);

            console.log(
              `Processing update ${i + 1}/${operatorUpdates.length}: ${
                update.operator.label || update.operator.channel_config_name
              } (${update.channel}/${update.country})`,
            );
            console.log(
              `Payload size: ${payloadSizeBytes} bytes | ${payloadSizeKB} KB`,
            );
            console.log(
              `Template ID being sent: ${update.translatedTemplate.template_id}`,
            );

            try {
              const response = await axios.post(
                `${adminUrl}/BulkUpdateTranslatedTemplate`,
                individualPayload,
              );

              if (response.data && response.status === 200) {
                successfulUpdates.push(update.operatorMappingId);
                console.log(
                  `✅ Success: ${
                    update.operator.label || update.operator.channel_config_name
                  } - Mapping ID: ${update.operatorMappingId}`,
                );
                const rowsAffected =
                  typeof response.data.rowsAffected === "string"
                    ? JSON.parse(response.data.rowsAffected)
                    : response.data.rowsAffected;

                // Check if any operators have auto_approval = true
                const autoApprovedOperators: {
                  operator_mapping_id: number;
                  operator_id: number;
                  auto_approval: boolean;
                }[] = rowsAffected?.auto_approved_operators ?? [];

                if (autoApprovedOperators.length > 0) {
                  // Loop through each auto approved operator and trigger webhook
                  for (const autoOperator of autoApprovedOperators) {
                    if (autoOperator.auto_approval === true) {
                      console.log(
                        `🔄 Triggering auto-approval webhook for operator_mapping_id: ${autoOperator.operator_mapping_id}`,
                      );
                      await triggerOperatorAutoApprovalWebhook(
                        autoOperator.operator_mapping_id,
                        autoOperator.operator_id,
                      );
                    }
                  }
                } else {
                  console.log(
                    `ℹ️ No auto-approval operators found. Rows affected: ${rowsAffected?.updated_count}`,
                  );
                }
              } else {
                console.error(
                  `❌ Failed: ${
                    update.operator.label || update.operator.channel_config_name
                  } - ${response.data?.Status_Description || "Unknown error"}`,
                );
                failedUpdates.push({
                  operatorMappingId: update.operatorMappingId,
                  operator:
                    update.operator.label ||
                    update.operator.channel_config_name ||
                    "Unknown",
                  channel: update.channel,
                  country: update.country,
                  error: response.data?.Status_Description || "Unknown error",
                });
              }

              // Add small delay between requests to avoid overwhelming the server
              if (i < operatorUpdates.length - 1) {
                await new Promise((resolve) => setTimeout(resolve, 10));
              }
            } catch (error: any) {
              console.error(
                `API Error for ${
                  update.operator.label || update.operator.channel_config_name
                }:`,
                error,
              );
              failedUpdates.push({
                operatorMappingId: update.operatorMappingId,
                operator:
                  update.operator.label ||
                  update.operator.channel_config_name ||
                  "Unknown",
                channel: update.channel,
                country: update.country,
                error:
                  error?.response?.data?.Status_Description ||
                  error.message ||
                  "Network error",
              });
            }
          }

          // Set status and message after processing all operators
          if (successfulUpdates.length > 0) {
            apiStatus = "Success";
            afterDataMessage = JSON.stringify({
              message: "Campaign updated successfully",
              campaign_id: campaignId,
              updated_operator_mapping_ids: successfulUpdates,
              total_updated: successfulUpdates.length,
              total_failed: failedUpdates.length,
              welcomeMessageDetails: operatorUpdates
                .filter((u) => successfulUpdates.includes(u.operatorMappingId))
                .map((u) => ({
                  operator:
                    u.operator.label ||
                    u.operator.channel_config_name ||
                    "Unknown",
                  channel: u.channel,
                  country: u.country,
                  welcomeMessageEnabled: u.operator?.welcomeMessage === true,
                  wm_template_id:
                    u.operator?.welcomeMessage === true
                      ? u.operator?.wm_template_id || null
                      : null,
                })),
            });
          } else {
            apiStatus = "Failed";
            afterDataMessage = JSON.stringify({
              message: "Campaign updation failed",
              campaign_id: campaignId,
              failed_operator_mapping_ids: failedUpdates.map(
                (f) => f.operatorMappingId,
              ),
              total_failed: failedUpdates.length,
              errors: failedUpdates.map((f) => ({
                operator_mapping_id: f.operatorMappingId,
                error: f.error,
              })),
            });
          }

          // Handle results
          if (successfulUpdates.length > 0) {
            console.log(
              `Successfully updated ${successfulUpdates.length} operators`,
            );

            // Trigger webhook for successful updates
            await triggerN8NWebhookForUpdate(successfulUpdates);

            if (failedUpdates.length === 0) {
              toast.toast({
                title: "Success",
                description: `${successfulUpdates.length} template(s) updated successfully`,
              });
            } else {
              toast.toast({
                title: "Partial Success",
                description: `${successfulUpdates.length} templates updated, ${failedUpdates.length} failed. Check console for details.`,
              });
              console.error("Failed updates:", failedUpdates);
            }

            // Insert audit history after successful updates
            // try {
            //   await axios.post(
            //     `${adminUrl}/InsertAuditHistroyforAdminResubmission`,
            //     null,
            //     {
            //       params: {
            //         user_account_id: accountId,
            //         campaign_id: campaignId,
            //         admin_workspace_id: admin_workspace_id,
            //       },
            //     }
            //   );
            //   console.log("✅ Audit history inserted successfully");
            // } catch (auditErr) {
            //   console.error("❌ Failed to insert audit history:", auditErr);
            //   toast.toast({
            //     title: "Audit Log Error",
            //     description: "Audit logging failed after template update.",
            //   });
            // }

            // Send operator notifications
            try {
              if (successfulUpdates.length > 0) {
                try {
                  const notifyResponse = await axios.post(
                    `${adminUrl}/NotifyOperatorsAdminResubmission`,
                    successfulUpdates,
                  );

                  if (notifyResponse.data?.Status === "Success") {
                    console.log(
                      "✅ Operator resubmission notifications sent successfully.",
                    );
                  } else {
                    console.warn(
                      "⚠️ Notification API did not return success:",
                      notifyResponse.data,
                    );
                  }
                } catch (notifyErr) {
                  console.error(
                    "❌ Failed to send operator resubmission notifications:",
                    notifyErr,
                  );
                }
              } else {
                console.log("ℹ️ No operator mapping IDs to notify.");
              }
            } catch (err) {
              console.error(
                "Unexpected error in resubmission notification flow:",
                err,
              );
            }

            setCampaignStatus("rejected");
            setTimeout(() => {
              navigate("/adminNavbar/campaigns");
            }, 500);
          } else {
            toast.toast({
              title: "Error",
              description: `No templates could be updated. ${failedUpdates.length} failures.`,
            });
            console.error("All updates failed:", failedUpdates);
          }
        } else {
          afterDataMessage = "No rejected templates to update";
          toast.toast({
            title: "Info",
            description: "No rejected templates to update",
          });
        }
      } catch (error: any) {
        apiStatus = "Failed";
        afterDataMessage = `Campaign updation failed. Campaign ID: ${campaignId}, Error: ${
          error?.message || "Unknown error"
        }`;

        console.error("Error updating templates:", error);
        toast.toast({
          title: "Error",
          description:
            error?.response?.data?.message || "Failed to update templates",
        });
      } finally {
        setIsLoading(false);

        try {
          const auditPayload = {
            UserAccountId: accountId,
            CampaignId: campaignId,
            AdvertiserWorkspaceId: admin_workspace_id,
            Action: "Campaign Updated by Admin",
            ActionPage: "Admin Campaign Approval",
            Status: apiStatus,
            AfterData: afterDataMessage,
            Comments:
              successfulUpdates.length > 0
                ? `Successfully updated ${
                    successfulUpdates.length
                  } operators. ${
                    failedUpdates.length > 0
                      ? `Failed: ${failedUpdates.length} operators.`
                      : ""
                  }`
                : failedUpdates.length > 0
                  ? `All updates failed. Total failures: ${failedUpdates.length}`
                  : "No updates attempted",
          };

          await axios.post(
            `${apiUrlAdvAcc}/InsertOverAllAuditHistory`,
            auditPayload,
          );
          console.log("✅ Overall audit history logged successfully");
        } catch (auditError) {
          console.error("❌ Failed to log overall audit history:", auditError);
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const triggerN8NWebhookForUpdate = async (mappingIds: number[]) => {
    try {
      const uniqueIds = Array.from(new Set(mappingIds));
      const payload = {
        operator_mapping_id: uniqueIds,
        campaignID: campaignId,
        campaignName: campaignName,
        advertiserID: userAccountId?.toString(),
        workspaceID: advertiserWorkspaceId?.toString(),
        wrokspaceName: adminsworkspaceName,
        request_type: "admin_update_campaign",
        comments: "Please approve the campaign",
        advertiser_email: advertiserEmail,
        channel_id: [""],
        channel_type: [""],
      };

      const response = await axios.post(
        `${adminUrl}/N8NAdminCampaignUpdateWebhook`,
        payload,
      );

      if (response.status !== 200) {
        console.error("❌ Webhook Trigger Failed");
      } else {
        console.log("✅ Webhook Triggered Successfully");
      }
    } catch (error) {
      console.error("❗ Error in triggerN8NWebhook", error);
    }
  };

  // Add this helper above the return statement
  const getWelcomeMessageApproveScenario = () => {
    const allOperators = Object.values(selectedOperators).flatMap(
      (countryMap) => Object.values(countryMap).flat(),
    );

    const totalCount = allOperators.length;
    const withWelcome = allOperators.filter(
      (op) => op.welcomeMessage === true,
    ).length;
    const withoutWelcome = totalCount - withWelcome;

    if (totalCount === 0) return "no_operators";
    if (withWelcome === totalCount) return "all_have_welcome"; // Scenario 1
    if (withoutWelcome === totalCount) return "none_have_welcome"; // Scenario 2
    return "partial"; // Scenario 3
  };

  return (
    <>
      {isInitialPageLoading ? (
        <div className="flex flex-col items-center justify-center h-[500px]">
          <CircularProgress color="primary" />
        </div>
      ) : isDeleted ? (
        <div className="flex flex-col items-center justify-center h-[500px] gap-4 text-center px-6">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-gray-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-8 h-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0a2 2 0 00-2-2H9a2 2 0 00-2 2m10 0H5"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-700">
            Campaign Deleted
          </h2>
          <p className="text-gray-500 max-w-md">
            This campaign was deleted and no more actions are required.
          </p>
          <Button
            variant="outline"
            className="w-[85px] border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white"
            onClick={() => navigate("/adminNavbar/campaigns")}
          >
            Back
          </Button>
        </div>
      ) : (
        <div>
          <RejectCampaignDialog
            open={showRejectDialog}
            comment={rejectComment}
            setComment={setRejectComment}
            onClose={() => setShowRejectDialog(false)}
            onConfirm={() => {
              setShowRejectDialog(false);
              console.log(rejectComment);
              handleReject();
            }}
          />

          <ApproveWithoutWelcomeDialog
            open={showApproveDialog}
            message={approveDialogMessage}
            onClose={() => setShowApproveDialog(false)}
            onConfirm={() => {
              setShowApproveDialog(false);
              handleApprove();
            }}
          />

          {/* Show internal loading overlay only for specific actions like approve/reject */}
          {isMappingLoading && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
              <div className="p-6 bg-white rounded-lg shadow-lg">
                <CircularProgress color="primary" />
                <p className="mt-4 text-center">Processing...</p>
              </div>
            </div>
          )}

          <div className="overflow-y-auto ml-[-7px]">
            <Toaster />
            <div className="fixed flex justify-end gap-4 mr-[40px] items-end right-[0px] top-[-15px] z-20">
              {/* PENDING STATUS - Show Reject and Approve buttons */}
              {!(
                campaignStatus === "already approved" ||
                campaignStatus === "rejected" ||
                campaignStatus === "admin rejected" ||
                campaignStatus === "mapping found and admin rejected" ||
                campaignStatusForSandbox === "sandbox"
              ) && (
                <>
                  <Button
                    variant="outline"
                    disabled={isLoading}
                    className="w-[85px] border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    onClick={() => setShowRejectDialog(true)}
                  >
                    {isLoading ? "Rejecting..." : "Reject"}
                  </Button>

                  <Button
                    className="w-[80px] text-[#F8FAFC]"
                    disabled={isMappingLoading || !allChannelsValid}
                    // onClick={() => {
                    //   const scenario = getWelcomeMessageApproveScenario();
                    //   if (scenario === 'all_have_welcome') {
                    //     handleApprove();
                    //   } else {
                    //     const allOperators = Object.values(
                    //       selectedOperators,
                    //     ).flatMap((countryMap) =>
                    //       Object.values(countryMap).flat(),
                    //     );
                    //     const withoutWelcome = allOperators.filter(
                    //       (op) => !op.welcomeMessage,
                    //     ).length;
                    //     const msg =
                    //       scenario === 'partial'
                    //         ? `${withoutWelcome} operator${withoutWelcome > 1 ? 's' : ''} ${withoutWelcome > 1 ? 'are' : 'is'} not configured with a welcome message. Do you want to proceed?`
                    //         : 'Approve without a welcome message?';
                    //     setApproveDialogMessage(msg);
                    //     setShowApproveDialog(true);
                    //   }
                    // }}
                    onClick={() => {
                      // Reuse Audience: WM not applicable, skip dialog entirely
                      if (audienceType === "Reuse Audience") {
                        handleApprove();
                        return;
                      }
                      const scenario = getWelcomeMessageApproveScenario();
                      if (scenario === "all_have_welcome") {
                        handleApprove();
                      } else {
                        const allOperators = Object.values(
                          selectedOperators,
                        ).flatMap((countryMap) =>
                          Object.values(countryMap).flat(),
                        );
                        const withoutWelcome = allOperators.filter(
                          (op) => !op.welcomeMessage,
                        ).length;
                        const msg =
                          scenario === "partial"
                            ? `${withoutWelcome} operator${withoutWelcome > 1 ? "s" : ""} ${withoutWelcome > 1 ? "are" : "is"} not configured with a welcome message. Do you want to proceed?`
                            : "Approve without a welcome message?";
                        setApproveDialogMessage(msg);
                        setShowApproveDialog(true);
                      }
                    }}
                  >
                    {isLoading ? "Approving..." : "Approve"}
                  </Button>
                </>
              )}

              {/* REJECTED / SANDBOX / ALREADY APPROVED WITH REJECTED OPERATORS - Show Update and Back/Reject buttons */}
              {(campaignStatus === "rejected" ||
                (campaignStatus === "already approved" &&
                  hasRejectedOperators) ||
                campaignStatusForSandbox === "sandbox") && (
                <>
                  {hasRejectedOperators &&
                    campaignStatus !== "mapping found and admin rejected" && (
                      <Button
                        disabled={isLoading}
                        className="w-[85px] text-[#F8FAFC]"
                        onClick={updateRejectedOperatorTemplates}
                      >
                        {isLoading ? "Updating..." : "Update"}
                      </Button>
                    )}

                  {campaignStatusForSandbox === "sandbox" &&
                  campaignStatus !== "rejected" &&
                  campaignStatus !== "already approved" &&
                  campaignStatus !== "mapping found and admin rejected" ? (
                    <Button
                      variant="outline"
                      disabled={isLoading}
                      className="w-[85px] border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                      onClick={handleSandboxReject}
                    >
                      {isLoading ? "Rejecting..." : "Reject"}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-[85px] border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white"
                      onClick={() => navigate("/adminNavbar/campaigns")}
                    >
                      Back
                    </Button>
                  )}
                </>
              )}

              {/* ADMIN REJECTED STATUS - Show only Back button */}
              {campaignStatus === "admin rejected" && (
                <Button
                  className="w-[85px] text-[#F8FAFC]"
                  onClick={() => navigate("/adminNavbar/campaigns")}
                >
                  Back
                </Button>
              )}

              {/* ALREADY APPROVED STATUS (no rejected operators) - Show only Back button */}
              {campaignStatus === "already approved" &&
                campaignStatusForSandbox !== "sandbox" &&
                !hasRejectedOperators && (
                  <Button
                    className="w-[85px] text-[#F8FAFC]"
                    onClick={() => navigate("/adminNavbar/campaigns")}
                  >
                    Back
                  </Button>
                )}

              {/* {campaignStatus === 'mapping found and admin rejected' && (
                <Button
                  className='w-[85px] text-[#F8FAFC]'
                  onClick={() => navigate('/adminNavbar/campaigns')}
                >
                  Back
                </Button>
              )} */}
            </div>

            <div className="flex gap-6 p-4 overflow-y-auto">
              <div className="flex-grow">
                <CampaignDetailsCard
                  campaignDetails={{
                    campaign_id: campaignId,
                    category_id: categoryId ?? 0,
                    category_sub_id: categorySubId ?? 0,
                    name: campaignName,
                    channels: availableChannels.map((ch) => ch.channel_name),
                    reachFrom: fromCountryNames,
                    reachTravelTo: toCountryName,
                    startDate,
                    endDate,
                    startTime,
                    endTime,
                    allocationType,
                    timeZone: toCountryTimezone,
                  }}
                  selectedOperators={selectedOperators}
                  setSelectedOperators={setSelectedOperators}
                  activeChannel={activeChannel}
                  setActiveChannel={setActiveChannel}
                  allocations={allocations}
                  editedAllocations={editedAllocations}
                  setEditedAllocations={setEditedAllocations}
                  operatorTemplates={operatorTemplates}
                  setOperatorTemplates={setOperatorTemplates}
                  allocationType={allocationType}
                  setAllocationType={setAllocationType}
                  roamingAge={roamingAge}
                  setRoamingAge={setRoamingAge}
                  isDuplicatePrevented={duplicateMsg}
                  setIsDuplicatePrevented={setDuplicateMsg}
                  isPremiumOnly={premiumNumber}
                  setIsPremiumOnly={setPremiumNumber}
                  availableChannels={availableChannels}
                  countryToIso={countryToIso}
                  channelNameToId={channelNameToId}
                  roamingDaysByCountry={roamingDaysByCountry}
                  setRoamingDaysByCountry={setRoamingDaysByCountry}
                  fromCountryISOs={fromCountryISOs}
                  fromCountries={fromCountries}
                  audienceType={audienceType}
                  selectedCampaignName={selectedCampaignName}
                  campaignStatus={campaignStatusForSandbox}
                />
              </div>
              <MessagePreviewSection
                operatorKeys={operatorKeys}
                selectedOperatorKey={selectedOperatorKey}
                setSelectedOperatorKey={setSelectedOperatorKey}
                operatorTemplates={operatorTemplates}
                allOperatorKeys={allOperatorKeys}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminCampaignApproval;
